import { authedFetch, getCsrfToken } from "@/lib/authed-fetch";
import { useState, useCallback, useEffect, useLayoutEffect, useRef, memo, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, Send, Hash, FolderKanban, Users, X, ImagePlus, Loader2, Search, MessageSquare, Globe, Plus, Minus, Paperclip, FileText, FileArchive, FileSpreadsheet, File as FileIcon, FileImage, Settings, Smile, Reply, Phone, Video, Bell, BellOff, Activity } from 'lucide-react';
import { ChatSearch } from './ChatSearch';
import { ScheduledMessageList } from './ScheduledMessageList';
import type { ScheduledMessageStatus } from '@shared/scheduled-message-status';
import { outstandingScheduledMessages } from '@shared/scheduled-message-status';
import { ChatMessageBubble } from './ChatMessageBubble';
import { ChatDockSettingsDialog } from './ChatDockSettingsDialog';
import { HudBubble, HudBubbleContainer } from './HudBubble';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { darkenColor } from '@/lib/color-utils';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/contexts/auth-context';
import { useWebSocket } from '@/contexts/websocket-context';
import { useToast } from '@/hooks/use-toast';
import { useProjectHud, useDockHud } from '@/hooks/use-unread-hud';
import { useProjects } from '@/hooks/use-shared-queries';
import { useStatusConfigs } from '@/hooks/use-status-configs';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthenticatedImage } from './AuthenticatedImage';
import { AttachmentPreviewModal, type PreviewAttachment } from './AttachmentPreviewModal';
import { audioNotifier, type ChatChannelType } from '@/lib/audio-notifier';
import type { Project, ProjectArtist } from '@shared/schema';

type ProjectWithArtists = Project & { artists?: ProjectArtist[] };
import { convertSmileyToEmoji, CHAT_EMOJI_OPTIONS } from '@/lib/emoji-utils';
import { isOutsideBusinessHours, getNextBusinessDayLabel } from '@/lib/business-hours';
import { Clock } from 'lucide-react';
import { buildMessageTree, type MessageWithReplies } from './thread-utils';
import { ThreadedMessageGroup } from './ThreadedMessageGroup';
import { ThreadActivityBanner } from './ThreadActivityBanner';
import { StatusBubble } from '@/components/status-bubble';
import { useCall } from '@/contexts/call-context';
import { getUserColor } from '@/lib/user-color';
import { ProjectHeaderVideoCall } from './ProjectHeaderVideoCall';
import type { ProjectCallMember } from './project-chat-recipients';
import {
  GROUP_CALL_LIMIT_TOAST,
  OPEN_PROJECT_CALL_EVENT,
  createProjectCallIntentConsumer,
  createProjectCallIntentQueue,
  decideProjectRowCallAction,
  exceedsCallParticipantLimit,
  filterAllowedInvitees,
  performStartGroupCall,
  performStartProjectChannelCall,
  resolveProjectCallIntent,
  toggleSelectedUserId,
  withIncludedAdmins,
  type ProjectCallIntent,
} from './project-call-actions';

interface DelayedSendState {
  panel: 'left' | 'right';
  content: string;
  rawContent: string;
  channelId: string;
  attachmentIds?: string[];
  replyToId?: string;
  timerId: ReturnType<typeof setTimeout>;
  startedAt: number;
  savedAttachments: PendingAttachment[];
}

interface ChatChannel {
  id: string;
  type: 'global' | 'group' | 'direct' | 'project' | 'topic' | 'client';
  name: string | null;
  projectId: string | null;
  unreadCount?: number;
  projectName?: string | null;
  lastInteractedAt?: string | null;
  lastActivitySource?: 'chat' | 'work' | null;
  isMuted?: boolean;
}

interface ChatAttachment {
  id: string;
  filename: string;
  originalFilename: string;
  dropboxSharedLink?: string | null;
  contentType: string;
  sizeBytes: number;
  thumbnailUrl?: string | null;
  metadata?: {
    width?: number;
    height?: number;
    isImage?: boolean;
    isVideo?: boolean;
    isAudio?: boolean;
  };
}

interface ChatMessageAuthor {
  id: number;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureUrl?: string | null;
  colorCode?: string | null;
}

interface ChatMessage {
  id: string;
  channelId: string;
  authorId: number | null;
  externalAuthorKind?: string | null;
  externalAuthorName?: string | null;
  author?: ChatMessageAuthor | null;
  content: string;
  richContent?: string;
  createdAt: string;
  attachments?: ChatAttachment[];
  replyToId?: string | null;
  metadata?: Record<string, unknown> | null;
  _optimistic?: boolean;
}

interface TeamMember extends ProjectCallMember {
  email?: string;
  profileImage?: string;
}

interface ProjectCandidate {
  projectId: string;
  projectName: string;
  existingChannel: ChatChannel | null;
  lastInteractedAt: string | null;
  lastActivitySource: 'chat' | 'work' | null;
  thumbnail: string | null;
  leadArtist: string | null;
  projectStatus: string | null;
  assignedArtists: TeamMember[];
  /** Active admins not already assigned to the project (caller excluded). */
  admins: TeamMember[];
}

function ensureUTCDate(val: string | Date): Date {
  if (val instanceof Date) return val;
  const str = String(val);
  if (!str.includes('Z') && !str.includes('+') && !/\d{2}:\d{2}$/.test(str)) {
    return new Date(str.replace(' ', 'T') + 'Z');
  }
  return new Date(str);
}

function formatCompactTimeAgo(dateStr: string): string | null {
  try {
    const date = ensureUTCDate(dateStr);
    if (isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return null;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 5) return `${diffWeeks}w`;
    const diffMonths = Math.floor(diffDays / 30.44);
    if (diffMonths < 12) return `${diffMonths}mo`;
    const diffYears = Math.floor(diffDays / 365.25);
    return `${diffYears}y`;
  } catch {
    return null;
  }
}

interface PendingAttachment {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  previewUrl?: string;
  attachmentId?: string;
  isImage: boolean;
  fileExtension: string;
  fileSize: string;
  mimeType: string;
}

function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() || '';
  return ext.length > 5 ? ext.slice(0, 5) : ext;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const REENCODABLE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const MAX_IMAGE_EDGE = 2000;
const JPEG_QUALITY = 0.9;

function swapImageExtension(name: string, outputType: string): string {
  const ext = outputType === 'image/png' ? 'png' : 'jpg';
  const base = name.replace(/\.[^/.]+$/, '');
  return `${base || 'image'}.${ext}`;
}

// Re-encode raster images in the browser before upload. Drawing to a canvas and
// re-exporting strips all ancillary metadata chunks (PNG tEXt/iTXt/XMP, JPEG EXIF)
// that the deployment proxy/WAF rejects, and downscales oversized images. The input
// format is preserved (PNG -> clean PNG, JPEG -> JPEG, WebP -> WebP) so transparency
// is kept; the PNG -> JPEG conversion only happens as a 403 fallback in
// uploadAttachment. Animated GIFs, SVGs and non-images are left untouched.
async function reencodeImageForUpload(file: File): Promise<File> {
  if (!REENCODABLE_IMAGE_TYPES.has(file.type)) {
    return file;
  }

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height) {
      return file;
    }

    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    // Preserve the input format; re-export only strips metadata and downscales.
    const outputType = file.type;
    const quality = outputType === 'image/png' ? undefined : JPEG_QUALITY;

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), outputType, quality);
    });
    if (!blob || blob.size === 0) {
      return file;
    }

    return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
  } catch (err) {
    console.warn('[ChatDock] Image re-encode failed, uploading original:', err);
    return file;
  } finally {
    bitmap?.close?.();
  }
}

// Flatten an image onto a white background and re-encode it as JPEG. Used as the
// production fallback when a clean PNG is still rejected by the deployment proxy
// (JPEG is confirmed to pass). Transparency is lost by design — only invoked once
// a PNG upload has already failed with a 403.
async function flattenImageToJpeg(file: File): Promise<File | null> {
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height) {
      return null;
    }

    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
    });
    if (!blob || blob.size === 0) {
      return null;
    }

    return new File([blob], swapImageExtension(file.name, 'image/jpeg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn('[ChatDock] PNG->JPEG flatten failed:', err);
    return null;
  } finally {
    bitmap?.close?.();
  }
}

function getFileIcon(mimeType: string, extension: string) {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType === 'application/pdf' || extension === 'PDF') return FileText;
  if (mimeType.includes('zip') || mimeType.includes('archive') || ['ZIP', 'RAR', '7Z', 'TAR', 'GZ'].includes(extension)) return FileArchive;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || ['XLS', 'XLSX', 'CSV'].includes(extension)) return FileSpreadsheet;
  if (mimeType.includes('document') || mimeType.includes('word') || ['DOC', 'DOCX', 'TXT', 'RTF'].includes(extension)) return FileText;
  return FileIcon;
}

type LeftPickerTab = 'projects';
type RightPickerTab = 'direct' | 'topics';

interface PanelState {
  channelId: string | null;
  inputValue: string;
  pickerTab: LeftPickerTab | RightPickerTab;
  pendingAttachments: PendingAttachment[];
  isDragging: boolean;
  replyToId: string | null;
  replyToAuthor: string | null;
}

interface PaginationState {
  beforeCursor: string | null;
  allMessages: ChatMessage[];
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface UserPreferences {
  chatLeftChannelId?: string | null;
  chatRightChannelId?: string | null;
}

export function ChatDock() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [leftPanel, setLeftPanel] = useState<PanelState>({ 
    channelId: null, 
    inputValue: '',
    pickerTab: 'projects',
    pendingAttachments: [],
    isDragging: false,
    replyToId: null,
    replyToAuthor: null,
  });
  const [rightPanel, setRightPanel] = useState<PanelState>({ 
    channelId: null, 
    inputValue: '',
    pickerTab: 'direct',
    pendingAttachments: [],
    isDragging: false,
    replyToId: null,
    replyToAuthor: null,
  });
  const [openPicker, setOpenPicker] = useState<'left' | 'right' | null>(null);
  const [projectCallIntent, setProjectCallIntent] = useState<ProjectCallIntent | null>(null);
  const projectCallIntentQueue = useRef(createProjectCallIntentQueue());
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [highlightMessageId, setHighlightMessageId] = useState<{ left: string | null; right: string | null }>({ left: null, right: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);
  const [showScheduleButton, setShowScheduleButton] = useState(() => isOutsideBusinessHours());
  const [scheduleLabel, setScheduleLabel] = useState(() => getNextBusinessDayLabel());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setShowScheduleButton(isOutsideBusinessHours());
      setScheduleLabel(getNextBusinessDayLabel());
    }, 60_000);
    return () => clearInterval(timer);
  }, []);
  
  // Pagination state for left and right panels
  const [leftPagination, setLeftPagination] = useState<PaginationState>({
    beforeCursor: null,
    allMessages: [],
    hasMore: true,
    isLoadingMore: false,
  });
  const [rightPagination, setRightPagination] = useState<PaginationState>({
    beforeCursor: null,
    allMessages: [],
    hasMore: true,
    isLoadingMore: false,
  });
  
  // New message highlight state - tracks if panel has unread new messages
  const [hasNewMessage, setHasNewMessage] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });
  
  // Clear highlight for a panel
  const clearHighlight = useCallback((panel: 'left' | 'right') => {
    setHasNewMessage(prev => ({ ...prev, [panel]: false }));
  }, []);
  
  // Collapse on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);
  
  // Set CSS variable for chat dock height (consumed by project-tree for bottom padding)
  useEffect(() => {
    const height = isExpanded ? 'calc(50vh + 20px)' : '100px';
    document.documentElement.style.setProperty('--chat-dock-height', height);
    return () => {
      document.documentElement.style.setProperty('--chat-dock-height', '100px');
    };
  }, [isExpanded]);
  
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { registerChannelMessageHandler, unregisterChannelMessageHandler, registerChannelDeleteHandler, unregisterChannelDeleteHandler } = useWebSocket();
  
  // Fetch user preferences for saved channel selections
  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
    enabled: !!user,
    staleTime: 60000,
  });

  const { data: channels = [], isLoading: isChannelsLoading, isError: isChannelsError } = useQuery<ChatChannel[]>({
    queryKey: ['/api/chat/channels'],
    enabled: !!user,
    staleTime: 30000,
  });

  const { data: unreadCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['/api/chat/channel-unread-counts'],
    enabled: !!user,
    staleTime: 10000,
    refetchInterval: 30000,
  });

  const { data: teamMembers = [], isLoading: isTeamMembersLoading, isError: isTeamMembersError } = useQuery<TeamMember[]>({
    queryKey: ['/api/users/team-members'],
    enabled: !!user,
    staleTime: 60000,
  });

  // Shared project roster used by both the project picker and the expanded
  // project-header call action. Keeping one query here prevents the header
  // from resolving recipients from a second, differently-shaped data source.
  const {
    data: allProjects = [],
    isLoading: isProjectsLoading,
    isError: isProjectsError,
  } = useProjects({ enabled: !!user }) as {
    data: ProjectWithArtists[];
    isLoading: boolean;
    isError: boolean;
  };

  // Fetch topic channels (admin-created, visible to all)
  const { data: topicChannels = [] } = useQuery<ChatChannel[]>({
    queryKey: ['/api/chat/channels/topics'],
    enabled: !!user,
    staleTime: 30000,
  });

  // Initial messages query (latest messages - no cursor)
  const { data: leftMessagesInitial = [], isFetching: isLeftFetchingInitial } = useQuery<ChatMessage[]>({
    queryKey: ['/api/chat/channels', leftPanel.channelId, 'messages'],
    enabled: !!leftPanel.channelId,
    staleTime: 5000,
  });

  const { data: rightMessagesInitial = [], isFetching: isRightFetchingInitial } = useQuery<ChatMessage[]>({
    queryKey: ['/api/chat/channels', rightPanel.channelId, 'messages'],
    enabled: !!rightPanel.channelId,
    staleTime: 5000,
  });
  
  // Paginated older messages query (with cursor) - includes error handling
  const { data: leftMessagesOlder = [], isFetching: isLeftFetchingOlder, isError: isLeftOlderError } = useQuery<ChatMessage[]>({
    queryKey: ['/api/chat/channels', leftPanel.channelId, 'messages', { before: leftPagination.beforeCursor }],
    enabled: !!leftPanel.channelId && !!leftPagination.beforeCursor,
    staleTime: Infinity, // Don't refetch older messages
    retry: 1,
  });

  const { data: rightMessagesOlder = [], isFetching: isRightFetchingOlder, isError: isRightOlderError } = useQuery<ChatMessage[]>({
    queryKey: ['/api/chat/channels', rightPanel.channelId, 'messages', { before: rightPagination.beforeCursor }],
    enabled: !!rightPanel.channelId && !!rightPagination.beforeCursor,
    staleTime: Infinity,
    retry: 1,
  });
  
  // Refs for cursor tracking (used in handleLoadMore to prevent duplicates)
  const leftCursorRef = useRef<string | null>(null);
  const rightCursorRef = useRef<string | null>(null);
  
  // Handle query errors - clear loading state but keep hasMore true to allow retry
  useEffect(() => {
    if (isLeftOlderError && leftPagination.isLoadingMore) {
      leftCursorRef.current = null; // Allow retry with same cursor
      setLeftPagination(prev => ({
        ...prev,
        beforeCursor: null, // Clear cursor to stop this fetch attempt
        isLoadingMore: false,
        // Keep hasMore: true to allow retry on next scroll
      }));
    }
  }, [isLeftOlderError, leftPagination.isLoadingMore]);
  
  useEffect(() => {
    if (isRightOlderError && rightPagination.isLoadingMore) {
      rightCursorRef.current = null; // Allow retry with same cursor
      setRightPagination(prev => ({
        ...prev,
        beforeCursor: null, // Clear cursor to stop this fetch attempt
        isLoadingMore: false,
        // Keep hasMore: true to allow retry on next scroll
      }));
    }
  }, [isRightOlderError, rightPagination.isLoadingMore]);
  
  // Reset pagination when channel changes
  useEffect(() => {
    leftCursorRef.current = null;
    setLeftPagination({
      beforeCursor: null,
      allMessages: [],
      hasMore: true,
      isLoadingMore: false,
    });
  }, [leftPanel.channelId]);
  
  useEffect(() => {
    rightCursorRef.current = null;
    setRightPagination({
      beforeCursor: null,
      allMessages: [],
      hasMore: true,
      isLoadingMore: false,
    });
  }, [rightPanel.channelId]);
  
  // Accumulate initial messages for left panel (merge with older messages on refetch)
  useEffect(() => {
    if (leftMessagesInitial.length > 0) {
      setLeftPagination(prev => {
        const newIds = new Set(leftMessagesInitial.map(m => m.id));
        const newClientIds = new Set(
          leftMessagesInitial
            .map(m => m.metadata?.clientMessageId as string | undefined)
            .filter(Boolean)
            .map(cid => `optimistic-${cid}`)
        );
        const olderMessages = prev.allMessages.filter(m =>
          !newIds.has(m.id) && !newClientIds.has(m.id)
        );
        return {
          ...prev,
          allMessages: [...leftMessagesInitial, ...olderMessages],
          hasMore: prev.beforeCursor ? prev.hasMore : leftMessagesInitial.length >= 50,
        };
      });
    }
  }, [leftMessagesInitial]);
  
  // Accumulate initial messages for right panel (merge with older messages on refetch)
  useEffect(() => {
    if (rightMessagesInitial.length > 0) {
      setRightPagination(prev => {
        const newIds = new Set(rightMessagesInitial.map(m => m.id));
        const newClientIds = new Set(
          rightMessagesInitial
            .map(m => m.metadata?.clientMessageId as string | undefined)
            .filter(Boolean)
            .map(cid => `optimistic-${cid}`)
        );
        const olderMessages = prev.allMessages.filter(m =>
          !newIds.has(m.id) && !newClientIds.has(m.id)
        );
        return {
          ...prev,
          allMessages: [...rightMessagesInitial, ...olderMessages],
          hasMore: prev.beforeCursor ? prev.hasMore : rightMessagesInitial.length >= 50,
        };
      });
    }
  }, [rightMessagesInitial]);
  
  // Handle older messages for left panel (including empty results)
  useEffect(() => {
    if (leftPagination.beforeCursor && leftPagination.isLoadingMore && !isLeftFetchingOlder && !isLeftOlderError) {
      // Clear cursor ref after successful completion to allow future pagination
      leftCursorRef.current = null;
      setLeftPagination(prev => {
        if (leftMessagesOlder.length === 0) {
          // No more messages - stop pagination
          return {
            ...prev,
            hasMore: false,
            isLoadingMore: false,
          };
        }
        const existingIds = new Set(prev.allMessages.map(m => m.id));
        const newMessages = leftMessagesOlder.filter(m => !existingIds.has(m.id));
        return {
          ...prev,
          allMessages: [...prev.allMessages, ...newMessages],
          hasMore: leftMessagesOlder.length >= 50,
          isLoadingMore: false,
        };
      });
    }
  }, [leftMessagesOlder, leftPagination.beforeCursor, leftPagination.isLoadingMore, isLeftFetchingOlder, isLeftOlderError]);
  
  // Handle older messages for right panel (including empty results)
  useEffect(() => {
    if (rightPagination.beforeCursor && rightPagination.isLoadingMore && !isRightFetchingOlder && !isRightOlderError) {
      // Clear cursor ref after successful completion to allow future pagination
      rightCursorRef.current = null;
      setRightPagination(prev => {
        if (rightMessagesOlder.length === 0) {
          // No more messages - stop pagination
          return {
            ...prev,
            hasMore: false,
            isLoadingMore: false,
          };
        }
        const existingIds = new Set(prev.allMessages.map(m => m.id));
        const newMessages = rightMessagesOlder.filter(m => !existingIds.has(m.id));
        return {
          ...prev,
          allMessages: [...prev.allMessages, ...newMessages],
          hasMore: rightMessagesOlder.length >= 50,
          isLoadingMore: false,
        };
      });
    }
  }, [rightMessagesOlder, rightPagination.beforeCursor, rightPagination.isLoadingMore, isRightFetchingOlder, isRightOlderError]);
  
  // Helper to sort messages by createdAt (with ID fallback for stability)
  const sortByCreatedAt = (messages: ChatMessage[]) => 
    [...messages].sort((a, b) => {
      const dateA = ensureUTCDate(a.createdAt).getTime();
      const dateB = ensureUTCDate(b.createdAt).getTime();
      if (dateA !== dateB) return dateA - dateB;
      return a.id.localeCompare(b.id); // Fallback for same timestamp
    });
  
  // Use accumulated messages in chronological order (oldest first for chat UI)
  // Sort by createdAt for guaranteed ordering regardless of fetch order
  const leftMessagesSorted = useMemo(() => sortByCreatedAt(leftPagination.allMessages), [leftPagination.allMessages]);
  const rightMessagesSorted = useMemo(() => sortByCreatedAt(rightPagination.allMessages), [rightPagination.allMessages]);
  
  // Export for render (use sorted versions)
  const leftMessages = leftMessagesSorted;
  const rightMessages = rightMessagesSorted;
  
  // Load more handlers - use sorted messages to find true oldest
  const handleLoadMoreLeft = useCallback(() => {
    if (leftPagination.hasMore && !leftPagination.isLoadingMore && leftMessagesSorted.length > 0) {
      // Get the chronologically oldest message (first in sorted array)
      const oldestMessage = leftMessagesSorted[0];
      // Prevent duplicate requests for the same cursor
      if (oldestMessage.id === leftCursorRef.current) return;
      leftCursorRef.current = oldestMessage.id;
      setLeftPagination(prev => ({
        ...prev,
        beforeCursor: oldestMessage.id,
        isLoadingMore: true,
      }));
    }
  }, [leftPagination.hasMore, leftPagination.isLoadingMore, leftMessagesSorted]);
  
  const handleLoadMoreRight = useCallback(() => {
    if (rightPagination.hasMore && !rightPagination.isLoadingMore && rightMessagesSorted.length > 0) {
      // Get the chronologically oldest message (first in sorted array)
      const oldestMessage = rightMessagesSorted[0];
      // Prevent duplicate requests for the same cursor
      if (oldestMessage.id === rightCursorRef.current) return;
      rightCursorRef.current = oldestMessage.id;
      setRightPagination(prev => ({
        ...prev,
        beforeCursor: oldestMessage.id,
        isLoadingMore: true,
      }));
    }
  }, [rightPagination.hasMore, rightPagination.isLoadingMore, rightMessagesSorted]);

  // HUD notification bubbles - each panel only filters out its own active channel
  // Left panel (projects) filters out the left panel's active channel only
  // Right panel (dock: global/direct/topic) filters out the right panel's active channel only
  const { channels: projectHudChannels } = useProjectHud();
  const { channels: dockHudChannels } = useDockHud();
  
  const filteredProjectHud = projectHudChannels.filter(c => !isExpanded || c.channelId !== leftPanel.channelId);
  const filteredDockHud = dockHudChannels.filter(c => !isExpanded || c.channelId !== rightPanel.channelId);

  const channelsWithUnread: ChatChannel[] = channels.map((channel) => ({
    ...channel,
    unreadCount: unreadCounts[channel.id] ?? 0,
  }));

  const globalChannel = channelsWithUnread.find(c => c.type === 'global');
  const projectChannels = channelsWithUnread.filter(c => c.type === 'project');
  const directChannels = channelsWithUnread.filter(c => c.type === 'direct');
  
  // Merge topic channels with unread counts
  const topicChannelsWithUnread: ChatChannel[] = topicChannels.map((channel) => ({
    ...channel,
    unreadCount: unreadCounts[channel.id] ?? 0,
  }));

  // Load saved channel selections from preferences
  useEffect(() => {
    if (channels.length > 0 && preferences && !preferencesLoaded) {
      setPreferencesLoaded(true);
      
      // Set left panel from saved preference (project channels only) or default to first project
      if (preferences.chatLeftChannelId && channels.some(c => c.id === preferences.chatLeftChannelId && c.type === 'project')) {
        setLeftPanel(prev => ({ ...prev, channelId: preferences.chatLeftChannelId! }));
      } else {
        const firstProject = channels.find(c => c.type === 'project');
        if (firstProject) {
          setLeftPanel(prev => ({ ...prev, channelId: firstProject.id }));
        }
      }
      
      // Set right panel from saved preference (check both regular channels AND topic channels)
      if (preferences.chatRightChannelId) {
        const foundInChannels = channels.some(c => c.id === preferences.chatRightChannelId);
        const foundInTopics = topicChannels.some(c => c.id === preferences.chatRightChannelId);
        if (foundInChannels || foundInTopics) {
          // For topic channels, join first before setting the channel to avoid 403s on message fetch
          if (foundInTopics) {
            const topicChannelId = preferences.chatRightChannelId!;
            apiRequest('POST', `/api/chat/channels/topics/${topicChannelId}/join`)
              .then(() => {
                // Clear any cached 403 errors before setting the channel
                queryClient.invalidateQueries({ queryKey: ['/api/chat/channels', topicChannelId, 'messages'] });
                setRightPanel(prev => ({ ...prev, channelId: topicChannelId }));
              })
              .catch(err => {
                console.error('[ChatDock] Failed to auto-join topic channel:', err);
                // Fallback to global channel on join failure
                const global = channels.find(c => c.type === 'global');
                if (global) {
                  setRightPanel(prev => ({ ...prev, channelId: global.id }));
                }
              });
          } else {
            setRightPanel(prev => ({ ...prev, channelId: preferences.chatRightChannelId! }));
          }
        } else {
          // Default to global channel for right panel
          const global = channels.find(c => c.type === 'global');
          if (global) {
            setRightPanel(prev => ({ ...prev, channelId: global.id }));
          }
        }
      } else {
        // Default to global channel if no preference
        const global = channels.find(c => c.type === 'global');
        if (global) {
          setRightPanel(prev => ({ ...prev, channelId: global.id }));
        }
      }
    }
  }, [channels, topicChannels, preferences, preferencesLoaded]);
  
  // Save channel selection to preferences when changed
  const saveChannelPreference = useCallback(async (panel: 'left' | 'right', channelId: string | null) => {
    try {
      const updates: Record<string, string | null> = panel === 'left' 
        ? { chatLeftChannelId: channelId }
        : { chatRightChannelId: channelId };
      await apiRequest('PATCH', '/api/user/preferences', updates);
    } catch (error) {
      console.error('[ChatDock] Failed to save channel preference:', error);
    }
  }, []);

  // Task 151: missed-call toast "Open chat" handler. Routes the channel into
  // the appropriate dock panel (direct → right; project → left; everything
  // else → right) without scrolling to a specific message — the missed-call
  // row will be among the latest in the channel.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { channelId?: string } | undefined;
      const targetId = detail?.channelId;
      if (!targetId) return;
      const allChannels = [...channels, ...topicChannels];
      const target = allChannels.find(c => c.id === targetId);
      if (!target) return;
      if (target.type === 'project') {
        setLeftPanel(prev => ({ ...prev, channelId: target.id }));
        void saveChannelPreference('left', target.id);
      } else {
        setRightPanel(prev => ({ ...prev, channelId: target.id }));
        void saveChannelPreference('right', target.id);
      }
    };
    window.addEventListener('missed-call:open-channel', handler);
    return () => window.removeEventListener('missed-call:open-channel', handler);
  }, [channels, topicChannels, saveChannelPreference]);

  // Refs for values used inside the channel message handler (avoids effect re-runs)
  const leftChannelIdRef = useRef(leftPanel.channelId);
  const rightChannelIdRef = useRef(rightPanel.channelId);
  const topicMutedRef = useRef(topicChannelsWithUnread);
  const userIdRef = useRef(user?.id);
  
  useEffect(() => {
    leftChannelIdRef.current = leftPanel.channelId;
    rightChannelIdRef.current = rightPanel.channelId;
    topicMutedRef.current = topicChannelsWithUnread;
    userIdRef.current = user?.id;
  });

  // V2 Chat Channel System: Subscribe to dedicated channel message events
  useEffect(() => {
    if (!registerChannelMessageHandler || !unregisterChannelMessageHandler) return;

    const handleChannelMessage = (data: any) => {
      const msgChannelId = data.channelId;
      const msgChannelType = data.channelType as ChatChannelType | undefined;
      const msgAuthorId = data.authorId;
      const mentionedUserIds: number[] = data.mentionedUserIds || [];
      const currentUserId = userIdRef.current;
      const currentLeftId = leftChannelIdRef.current;
      const currentRightId = rightChannelIdRef.current;
      const incomingClientMessageId = data.clientMessageId as string | undefined;
      const serverMessage = data.message;
      
      const isChannelMuted = msgChannelType === 'topic' && 
        topicMutedRef.current.some(c => c.id === msgChannelId && c.isMuted);
      
      if (msgAuthorId !== currentUserId && !isChannelMuted) {
        const isMentioned = currentUserId && mentionedUserIds.includes(currentUserId);
        if (isMentioned) {
          audioNotifier.playByType('mention');
        } else if (msgChannelType) {
          audioNotifier.playByChatChannelType(msgChannelType);
        }
        
        if (msgChannelId === currentLeftId) {
          setHasNewMessage(prev => ({ ...prev, left: true }));
        }
        if (msgChannelId === currentRightId) {
          setHasNewMessage(prev => ({ ...prev, right: true }));
        }
      }
      
      const deduplicateOrInvalidate = (channelId: string) => {
        if (incomingClientMessageId && serverMessage) {
          const optimisticId = `optimistic-${incomingClientMessageId}`;
          const existing = queryClient.getQueryData<ChatMessage[]>(['/api/chat/channels', channelId, 'messages']);
          if (existing?.some(m => m.id === optimisticId)) {
            queryClient.setQueryData<ChatMessage[]>(
              ['/api/chat/channels', channelId, 'messages'],
              (old = []) => old.map(msg =>
                msg.id === optimisticId ? { ...serverMessage, _optimistic: false } : msg
              )
            );
            return;
          }
          if (existing?.some(m => m.id === serverMessage.id)) {
            return;
          }
        }
        queryClient.invalidateQueries({ 
          queryKey: ['/api/chat/channels', channelId, 'messages'] 
        });
      };
      
      if (currentLeftId && msgChannelId === currentLeftId) {
        deduplicateOrInvalidate(currentLeftId);
      }
      if (currentRightId && msgChannelId === currentRightId) {
        deduplicateOrInvalidate(currentRightId);
      }
      
      queryClient.invalidateQueries({ 
        queryKey: ['/api/chat/channel-unread-counts'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['/api/chat/unread-counts-all'] 
      });
    };

    registerChannelMessageHandler(handleChannelMessage);
    return () => unregisterChannelMessageHandler();
  }, [registerChannelMessageHandler, unregisterChannelMessageHandler, queryClient]);

  useEffect(() => {
    if (!registerChannelDeleteHandler || !unregisterChannelDeleteHandler) return;

    const handleChannelDelete = (data: any) => {
      const { messageId, channelId } = data;
      if (!messageId) return;
      if (channelId && leftChannelIdRef.current === channelId) {
        setLeftPagination(prev => ({
          ...prev,
          allMessages: prev.allMessages.filter(m => m.id !== messageId),
        }));
      }
      if (channelId && rightChannelIdRef.current === channelId) {
        setRightPagination(prev => ({
          ...prev,
          allMessages: prev.allMessages.filter(m => m.id !== messageId),
        }));
      }
    };

    registerChannelDeleteHandler(handleChannelDelete);
    return () => unregisterChannelDeleteHandler();
  }, [registerChannelDeleteHandler, unregisterChannelDeleteHandler]);

  const handleDeleteMessage = useCallback(async (messageId: string, messageContent: string, channelId: string) => {
    if (!channelId) return;

    try {
      await navigator.clipboard.writeText(messageContent || '');
    } catch (e) {
    }

    const previousMessages = queryClient.getQueryData<any[]>(['/api/chat/channels', channelId, 'messages']);
    let snapshotLeft: any[] = [];
    let snapshotRight: any[] = [];

    queryClient.setQueryData<any[]>(
      ['/api/chat/channels', channelId, 'messages'],
      (old) => old ? old.filter(m => m.id !== messageId) : old
    );
    setLeftPagination(prev => {
      snapshotLeft = prev.allMessages;
      return { ...prev, allMessages: prev.allMessages.filter(m => m.id !== messageId) };
    });
    setRightPagination(prev => {
      snapshotRight = prev.allMessages;
      return { ...prev, allMessages: prev.allMessages.filter(m => m.id !== messageId) };
    });

    try {
      await apiRequest('DELETE', `/api/chat/channels/${channelId}/messages/${messageId}`);
    } catch (error) {
      if (previousMessages) {
        queryClient.setQueryData(['/api/chat/channels', channelId, 'messages'], previousMessages);
      }
      setLeftPagination(prev => ({ ...prev, allMessages: snapshotLeft }));
      setRightPagination(prev => ({ ...prev, allMessages: snapshotRight }));
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels', channelId, 'messages'] });
    }
  }, [queryClient]);

  const uploadAttachment = useCallback(async (
    channelId: string,
    file: File,
    panel: 'left' | 'right',
    tempId: string
  ): Promise<ChatAttachment | null> => {
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;

    // Deduplicate: only write state when the rounded percentage actually
    // changes, so fast uploads don't thrash the (large) ChatDock tree.
    let lastPct = -1;
    const setProgress = (pct: number) => {
      if (pct === lastPct) return;
      lastPct = pct;
      setState(prev => ({
        ...prev,
        pendingAttachments: prev.pendingAttachments.map(a =>
          a.id === tempId ? { ...a, progress: pct } : a
        ),
      }));
    };

    // XHR instead of fetch: fetch has no upload progress events, so large
    // files (or small files on slow connections) showed only a spinner.
    // Returns a minimal Response-like shape the rest of the flow consumes.
    const doUpload = (uploadFile: File): Promise<{ ok: boolean; status: number; text: () => Promise<string>; json: () => Promise<any> }> => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', uploadFile);
        const xhr = new XMLHttpRequest();
        activeUploadsRef.current.add(xhr);
        const done = () => activeUploadsRef.current.delete(xhr);
        xhr.open('POST', `/api/chat/channels/${channelId}/attachments`);
        xhr.withCredentials = true;
        const csrf = getCsrfToken();
        if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          // Cap at 99: the last percent flips only when the server confirms.
          setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        };
        xhr.onload = () => { done(); resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          text: async () => xhr.responseText,
          json: async () => JSON.parse(xhr.responseText),
        }); };
        xhr.onerror = () => { done(); reject(new Error('Network error during upload')); };
        xhr.onabort = () => { done(); reject(new Error('Upload aborted')); };
        xhr.send(formData);
      });
    };

    try {
      let uploadedFile = file;
      let response = await doUpload(uploadedFile);

      // Expired csrf-token cookie: refresh re-issues it, retry once
      // (mirrors authedFetch's recovery path, which XHR bypasses).
      if (response.status === 403) {
        try {
          const body = JSON.parse(await response.text());
          if (body?.code === 'csrf_token_mismatch') {
            const { refreshAuthToken } = await import('@/lib/auth');
            if (await refreshAuthToken()) {
              setProgress(0);
              response = await doUpload(uploadedFile);
            }
          }
        } catch { /* not JSON — fall through to the PNG fallback below */ }
      }

      // Production proxy/WAF fallback: a clean re-encoded PNG can still be rejected
      // with a 403 before the request reaches Express. JPEG is confirmed to pass the
      // proxy, so flatten any transparency onto white, re-encode as JPEG, and retry once.
      if (response.status === 403 && uploadedFile.type === 'image/png') {
        const jpegFile = await flattenImageToJpeg(uploadedFile);
        if (jpegFile) {
          console.warn('[ChatDock] PNG upload returned 403, retrying as JPEG');
          uploadedFile = jpegFile;
          response = await doUpload(uploadedFile);
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatDock] Upload failed:', response.status, errorText);
        throw new Error(`Upload failed (${response.status})${errorText ? `: ${errorText.slice(0, 200)}` : ''}`);
      }
      
      const attachment = await response.json();
      
      setState(prev => ({
        ...prev,
        pendingAttachments: prev.pendingAttachments.map(a =>
          a.id === tempId ? {
            ...a,
            attachmentId: attachment.id,
            status: 'completed' as const,
            progress: 100,
            mimeType: uploadedFile.type || a.mimeType,
            fileExtension: getFileExtension(uploadedFile.name),
            fileSize: formatFileSize(uploadedFile.size),
          } : a
        ),
      }));
      
      return attachment;
    } catch (error) {
      console.error('[ChatDock] Upload error:', error);
      toast({
        title: 'Image upload failed',
        description: error instanceof Error ? error.message : 'Could not upload the file. Please try again.',
        variant: 'destructive',
      });
      setState(prev => ({
        ...prev,
        pendingAttachments: prev.pendingAttachments.map(a =>
          a.id === tempId ? { ...a, status: 'error' as const } : a
        ),
      }));
      return null;
    }
  }, []);

  const { toast } = useToast();
  
  const pendingStateRef = useRef<{ panel: 'left' | 'right'; attachments: PendingAttachment[] } | null>(null);

  // In-flight attachment uploads; aborted on unmount so their progress/
  // completion callbacks can't fire state setters afterwards.
  const activeUploadsRef = useRef<Set<XMLHttpRequest>>(new Set());
  useEffect(() => () => {
    for (const xhr of activeUploadsRef.current) { try { xhr.abort(); } catch {} }
    activeUploadsRef.current.clear();
  }, []);

  const isPastedLeftRef = useRef(false);
  const isPastedRightRef = useRef(false);

  const [delayedSend, setDelayedSend] = useState<DelayedSendState | null>(null);
  const delayedSendRef = useRef<DelayedSendState | null>(null);

  useEffect(() => {
    return () => {
      if (delayedSendRef.current) {
        clearTimeout(delayedSendRef.current.timerId);
      }
    };
  }, []);

  useEffect(() => {
    if (!delayedSendRef.current) return;
    const ds = delayedSendRef.current;
    const currentChannelId = ds.panel === 'left' ? leftPanel.channelId : rightPanel.channelId;
    if (currentChannelId !== ds.channelId) {
      clearTimeout(ds.timerId);
      delayedSendRef.current = null;
      setDelayedSend(null);
    }
  }, [leftPanel.channelId, rightPanel.channelId]);

  const sendMessageMutation = useMutation({
    mutationFn: async ({ channelId, content, attachmentIds, replyToId, clientMessageId }: { channelId: string; content: string; attachmentIds?: string[]; replyToId?: string; clientMessageId: string }) => {
      const response = await apiRequest('POST', `/api/chat/channels/${channelId}/messages`, {
        content: content.trim(),
        attachmentIds,
        replyToId,
        clientMessageId,
      });
      return response.json();
    },
    onMutate: async ({ channelId, content, clientMessageId }) => {
      await queryClient.cancelQueries({ queryKey: ['/api/chat/channels', channelId, 'messages'] });
      
      const optimisticMessage: ChatMessage = {
        id: `optimistic-${clientMessageId}`,
        channelId,
        authorId: user?.id || 0,
        author: user ? {
          id: user.id,
          username: user.username || '',
          firstName: user.firstName || null,
          lastName: user.lastName || null,
          profilePictureUrl: null,
          colorCode: null,
        } : null,
        content: content.trim(),
        createdAt: new Date().toISOString(),
        metadata: { clientMessageId },
        _optimistic: true,
      };

      queryClient.setQueryData<ChatMessage[]>(
        ['/api/chat/channels', channelId, 'messages'],
        (old = []) => [...old, optimisticMessage]
      );
    },
    onSuccess: (serverMessage, { channelId, clientMessageId }) => {
      if (pendingStateRef.current) {
        pendingStateRef.current.attachments.forEach(a => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        });
      }
      pendingStateRef.current = null;

      const optimisticId = `optimistic-${clientMessageId}`;
      const existing = queryClient.getQueryData<ChatMessage[]>(['/api/chat/channels', channelId, 'messages']);
      if (existing?.some(m => m.id === optimisticId)) {
        queryClient.setQueryData<ChatMessage[]>(
          ['/api/chat/channels', channelId, 'messages'],
          (old = []) => old.map(msg =>
            msg.id === optimisticId ? { ...serverMessage, _optimistic: false } : msg
          )
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: ['/api/chat/channels', channelId, 'messages']
        });
      }
      queryClient.invalidateQueries({ 
        queryKey: ['/api/chat/channel-unread-counts'] 
      });
    },
    onError: (error: Error, { channelId, clientMessageId }) => {
      const optimisticId = `optimistic-${clientMessageId}`;
      queryClient.setQueryData<ChatMessage[]>(
        ['/api/chat/channels', channelId, 'messages'],
        (old = []) => old.filter(msg => msg.id !== optimisticId)
      );
      if (pendingStateRef.current) {
        const { panel, attachments } = pendingStateRef.current;
        const setState = panel === 'left' ? setLeftPanel : setRightPanel;
        setState(prev => ({ ...prev, pendingAttachments: attachments }));
        pendingStateRef.current = null;
      }
      toast({
        title: 'Failed to send message',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const executeDelayedSend = useCallback((capturedChannelId: string, capturedContent: string, capturedAttachmentIds?: string[], capturedReplyToId?: string) => {
    const clientMessageId = crypto.randomUUID();
    sendMessageMutation.mutate({
      channelId: capturedChannelId,
      content: capturedContent,
      attachmentIds: capturedAttachmentIds,
      replyToId: capturedReplyToId,
      clientMessageId,
    });
  }, [sendMessageMutation]);

  const cancelDelayedSend = useCallback(() => {
    if (delayedSendRef.current) {
      const { panel, rawContent, timerId, savedAttachments } = delayedSendRef.current;
      clearTimeout(timerId);
      const setState = panel === 'left' ? setLeftPanel : setRightPanel;
      setState(prev => ({ ...prev, inputValue: rawContent, pendingAttachments: savedAttachments }));
      const ref = panel === 'left' ? isPastedLeftRef : isPastedRightRef;
      ref.current = false;
      delayedSendRef.current = null;
      setDelayedSend(null);
    }
  }, []);

  const handleSend = useCallback((panel: 'left' | 'right') => {
    const state = panel === 'left' ? leftPanel : rightPanel;
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    
    const completedAttachments = state.pendingAttachments
      .filter(a => a.status === 'completed')
      .map(a => a.attachmentId!)
      .filter(Boolean);
    
    const hasContent = state.inputValue.trim();
    const hasAttachments = completedAttachments.length > 0;
    const hasUploading = state.pendingAttachments.some(a => a.status === 'uploading');
    const hasFailedAttachments = state.pendingAttachments.some(a => a.status === 'error');
    
    if (hasUploading) {
      toast({
        title: 'Still uploading',
        description: 'Please wait until the image finishes uploading before sending.',
      });
      return;
    }

    if (hasFailedAttachments && !hasContent && !hasAttachments) {
      toast({
        title: 'Image upload failed',
        description: 'The image could not be uploaded, so there is nothing to send. Please try adding it again.',
        variant: 'destructive',
      });
      return;
    }
    
    if (state.channelId && (hasContent || hasAttachments)) {
      const convertedContent = convertSmileyToEmoji(state.inputValue);
      const pastedRef = panel === 'left' ? isPastedLeftRef : isPastedRightRef;
      const isGlobal = state.channelId === globalChannel?.id;
      const isAdminUser = user?.role === 'admin';

      if (isAdminUser && isGlobal && pastedRef.current) {
        if (delayedSendRef.current) {
          clearTimeout(delayedSendRef.current.timerId);
        }

        const capturedChannelId = state.channelId;
        const capturedAttachmentIds = hasAttachments ? completedAttachments : undefined;
        const capturedReplyToId = state.replyToId || undefined;
        const capturedContent = convertedContent;
        const capturedRawContent = state.inputValue;
        const capturedAttachments = [...state.pendingAttachments];

        const timerId = setTimeout(() => {
          pendingStateRef.current = { panel, attachments: capturedAttachments };
          executeDelayedSend(capturedChannelId, capturedContent, capturedAttachmentIds, capturedReplyToId);
          pastedRef.current = false;
          delayedSendRef.current = null;
          setDelayedSend(null);
        }, 5000);

        const delayState: DelayedSendState = {
          panel,
          content: convertedContent,
          rawContent: capturedRawContent,
          channelId: capturedChannelId,
          attachmentIds: capturedAttachmentIds,
          replyToId: capturedReplyToId,
          timerId,
          startedAt: Date.now(),
          savedAttachments: capturedAttachments,
        };
        delayedSendRef.current = delayState;
        setDelayedSend(delayState);

        setState(prev => ({ ...prev, inputValue: '', pendingAttachments: [], replyToId: null, replyToAuthor: null }));
        return;
      }

      pendingStateRef.current = { panel, attachments: [...state.pendingAttachments] };
      
      const clientMessageId = crypto.randomUUID();
      sendMessageMutation.mutate({ 
        channelId: state.channelId, 
        content: convertedContent,
        attachmentIds: hasAttachments ? completedAttachments : undefined,
        replyToId: state.replyToId || undefined,
        clientMessageId,
      });
      setState(prev => ({ ...prev, inputValue: '', pendingAttachments: [], replyToId: null, replyToAuthor: null }));
      pastedRef.current = false;
    }
  }, [leftPanel, rightPanel, sendMessageMutation, toast, globalChannel?.id, user?.role, executeDelayedSend]);

  const handleScheduleSend = useCallback(async (panel: 'left' | 'right') => {
    const state = panel === 'left' ? leftPanel : rightPanel;
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    
    const completedAttachments = state.pendingAttachments
      .filter(a => a.status === 'completed')
      .map(a => a.attachmentId!)
      .filter(Boolean);
    
    const hasContent = state.inputValue.trim();
    const hasAttachments = completedAttachments.length > 0;
    const hasUploading = state.pendingAttachments.some(a => a.status === 'uploading');
    
    if (hasUploading) return;
    
    if (state.channelId && (hasContent || hasAttachments)) {
      try {
        const convertedContent = convertSmileyToEmoji(state.inputValue);
        const response = await apiRequest('POST', `/api/chat/channels/${state.channelId}/messages/schedule`, {
          content: convertedContent.trim(),
          attachmentIds: hasAttachments ? completedAttachments : undefined,
          replyToId: state.replyToId || undefined,
        });
        const result = await response.json();
        
        setState(prev => ({ ...prev, inputValue: '', pendingAttachments: [], replyToId: null, replyToAuthor: null }));
        
        queryClient.invalidateQueries({
          queryKey: ['/api/chat/channels', state.channelId, 'messages', 'scheduled']
        });
        
        const label = result.scheduledForLabel || scheduleLabel;
        toast({
          title: 'Message scheduled',
          description: `Will be sent on ${label}`,
        });
      } catch (error: any) {
        toast({
          title: 'Planning failed',
          description: error.message || 'Please try again',
          variant: 'destructive',
        });
      }
    }
  }, [leftPanel, rightPanel, scheduleLabel, toast, queryClient]);

  const handleJumpToMessage = useCallback(async (panel: 'left' | 'right', messageId: string, overrideChannelId?: string) => {
    const state = panel === 'left' ? leftPanel : rightPanel;
    const channelId = overrideChannelId || state.channelId;
    if (!channelId) return;

    setIsExpanded(true);

    try {
      const aroundResult = await queryClient.fetchQuery({
        queryKey: ['/api/chat/channels', channelId, 'messages', 'around', messageId],
        queryFn: async () => {
          const res = await apiRequest(
            'GET',
            `/api/chat/channels/${channelId}/messages?aroundMessageId=${messageId}&beforeCount=25&afterCount=25`
          );
          return await res.json() as {
            messages: ChatMessage[];
            targetIndex: number;
            hasMoreBefore: boolean;
            hasMoreAfter: boolean;
          };
        },
        staleTime: 0,
      });

      if (!aroundResult.messages || aroundResult.targetIndex < 0) {
        toast({
          title: 'Message not found',
          description: 'Could not locate the message',
          variant: 'destructive',
        });
        return;
      }

      const currentMessages: ChatMessage[] = queryClient.getQueryData(
        ['/api/chat/channels', channelId, 'messages']
      ) || [];

      const existingIds = new Set(currentMessages.map(m => m.id));
      const newMessages = aroundResult.messages.filter(m => !existingIds.has(m.id));
      
      if (newMessages.length > 0) {
        const mergedMessages = [...currentMessages, ...newMessages].sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return ensureUTCDate(a.createdAt).getTime() - ensureUTCDate(b.createdAt).getTime();
        });
        queryClient.setQueryData(
          ['/api/chat/channels', channelId, 'messages'],
          mergedMessages
        );
      }

      const attemptScroll = (attempts: number = 0) => {
        const scrollRef = panel === 'left' ? leftScrollRef : rightScrollRef;
        if (scrollRef.current) {
          const targetElement = scrollRef.current.querySelector(`[data-message-id="${messageId}"]`);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'instant', block: 'center' });
            targetElement.classList.add('highlight-message');
            setTimeout(() => {
              targetElement.classList.remove('highlight-message');
            }, 2000);
            return;
          }
        }
        if (attempts < 5) {
          setTimeout(() => attemptScroll(attempts + 1), 50);
        }
      };

      setTimeout(() => attemptScroll(), 50);
    } catch (error: any) {
      console.error('[ChatDock] Failed to jump to message:', error);
      toast({
        title: 'Failed to load message',
        description: 'Could not navigate to the selected message',
        variant: 'destructive',
      });
    }
  }, [leftPanel, rightPanel, queryClient, toast]);

  useEffect(() => {
    const handleOpenProjectChannel = async (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string; targetMessageId?: number | null; channelId?: string; channelType?: string }>;
      const { projectId, targetMessageId, channelId, channelType } = customEvent.detail;
      
      if (channelId) {
        const isProjectChannel = channelType === 'project';
        const panel = isProjectChannel ? 'left' : 'right';
        const setPanel = isProjectChannel ? setLeftPanel : setRightPanel;
        
        console.log('[ChatDock] Fast path: opening channel directly:', channelId, 'type:', channelType, 'panel:', panel);
        queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
        setPanel(prev => ({ ...prev, channelId }));
        setIsExpanded(true);

        if (targetMessageId) {
          handleJumpToMessage(panel, targetMessageId.toString(), channelId);
        }
        return;
      }

      try {
        const response = await apiRequest('GET', `/api/chat/channels/project/${projectId}`);
        if (response.ok) {
          const channel = await response.json();
          queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
          setLeftPanel(prev => ({ ...prev, channelId: channel.id }));
          setIsExpanded(true);

          if (targetMessageId) {
            handleJumpToMessage('left', targetMessageId.toString(), channel.id);
          }
        }
      } catch (error) {
        console.error('[ChatDock] Error opening project channel:', error);
      }
    };
    
    window.addEventListener('open-project-channel', handleOpenProjectChannel);
    return () => window.removeEventListener('open-project-channel', handleOpenProjectChannel);
  }, [queryClient, handleJumpToMessage]);

  useEffect(() => {
    const handleOpenProjectCall = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: unknown; type?: unknown }>).detail;
      if (!detail || typeof detail.projectId !== 'string' || !detail.projectId.trim() || detail.type !== 'video') {
        toast({
          title: 'Could not start video call',
          description: 'Choose a valid project before starting a call.',
          variant: 'destructive',
        });
        return;
      }
      // The request is intentionally resolved later by the left project picker:
      // its project list is the client-side authorization boundary. Do not use
      // the panel's previous project selection as a fallback.
      const intent = projectCallIntentQueue.current.tryAccept({
        projectId: detail.projectId,
        type: 'video',
      });
      if (!intent) {
        toast({
          title: 'Video call already pending',
          description: 'Please wait for the current project call request to finish loading.',
          variant: 'destructive',
        });
        return;
      }
      setProjectCallIntent(intent);
      setIsExpanded(true);
    };

    window.addEventListener(OPEN_PROJECT_CALL_EVENT, handleOpenProjectCall);
    return () => window.removeEventListener(OPEN_PROJECT_CALL_EVENT, handleOpenProjectCall);
  }, [toast]);

  const consumeProjectCallIntent = useCallback((requestId: number) => {
    if (!projectCallIntentQueue.current.consume(requestId)) return;
    setProjectCallIntent((current) => current?.requestId === requestId ? null : current);
  }, []);

  const openProjectCallRecipientPicker = useCallback(() => {
    setOpenPicker('left');
  }, []);

  const handleFileDrop = useCallback(async (panel: 'left' | 'right', files: FileList | File[]) => {
    const state = panel === 'left' ? leftPanel : rightPanel;
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    
    if (!state.channelId) return;
    
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    
    for (const originalFile of fileArray) {
      const file = await reencodeImageForUpload(originalFile);
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const mimeType = file.type || 'application/octet-stream';
      const isImage = mimeType.startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
      
      setState(prev => ({
        ...prev,
        pendingAttachments: [...prev.pendingAttachments, {
          id: tempId,
          file,
          progress: 0,
          status: 'uploading',
          previewUrl,
          isImage,
          fileExtension: getFileExtension(file.name),
          fileSize: formatFileSize(file.size),
          mimeType,
        }],
        isDragging: false,
      }));
      
      await uploadAttachment(state.channelId, file, panel, tempId);
    }
  }, [leftPanel.channelId, rightPanel.channelId, uploadAttachment]);

  const handlePaste = useCallback((panel: 'left' | 'right', e: React.ClipboardEvent) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      handleFileDrop(panel, files);
      return;
    }
    const textData = e.clipboardData?.getData('text/plain');
    if (textData && textData.length > 0) {
      const ref = panel === 'left' ? isPastedLeftRef : isPastedRightRef;
      ref.current = true;
    }
  }, [handleFileDrop]);

  const fileInputLeftRef = useRef<HTMLInputElement>(null);
  const fileInputRightRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = useCallback((panel: 'left' | 'right', e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileDrop(panel, files);
    }
    e.target.value = '';
  }, [handleFileDrop]);

  const handleDragOver = useCallback((panel: 'left' | 'right', e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    setState(prev => ({ ...prev, isDragging: true }));
  }, []);

  const handleDragLeave = useCallback((panel: 'left' | 'right', e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    setState(prev => ({ ...prev, isDragging: false }));
  }, []);

  const handleDrop = useCallback((panel: 'left' | 'right', e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    setState(prev => ({ ...prev, isDragging: false }));
    
    if (e.dataTransfer.files.length > 0) {
      handleFileDrop(panel, e.dataTransfer.files);
    }
  }, [handleFileDrop]);

  const removePendingAttachment = useCallback(async (panel: 'left' | 'right', tempId: string) => {
    const state = panel === 'left' ? leftPanel : rightPanel;
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    
    const attachment = state.pendingAttachments.find(a => a.id === tempId);
    
    setState(prev => ({
      ...prev,
      pendingAttachments: prev.pendingAttachments.filter(a => a.id !== tempId),
    }));
    
    if (attachment?.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
    
    if (attachment?.attachmentId && state.channelId) {
      try {
        await apiRequest('DELETE', `/api/chat/channels/${state.channelId}/attachments/${attachment.attachmentId}`);
      } catch (error) {
        console.error('[ChatDock] Failed to delete attachment:', error);
      }
    }
  }, [leftPanel, rightPanel]);

  const handleInputChange = useCallback((panel: 'left' | 'right', value: string) => {
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    setState(prev => ({ ...prev, inputValue: value }));
    if (value === '') {
      const ref = panel === 'left' ? isPastedLeftRef : isPastedRightRef;
      ref.current = false;
    }
  }, []);

  // Client-mode channels (task #74) may not be in the channels query cache
  // immediately after creation — keep the fetched objects around so the
  // left panel can render them right away.
  const [clientChannelCache, setClientChannelCache] = useState<Record<string, ChatChannel>>({});

  const handleChannelSelect = useCallback(async (panel: 'left' | 'right', channelId: string, firstUnreadMessageId?: string | null) => {
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    
    // Check if this is a topic channel and auto-join if needed
    const isTopicChannel = topicChannels.some(c => c.id === channelId);
    if (isTopicChannel) {
      try {
        await apiRequest('POST', `/api/chat/channels/topics/${channelId}/join`);
        // Clear any cached 403 errors for this channel's messages
        queryClient.invalidateQueries({ queryKey: ['/api/chat/channels', channelId, 'messages'] });
      } catch (error) {
        console.error('[ChatDock] Failed to join topic channel:', error);
        return; // Don't select the channel if join failed
      }
    }
    
    // Expand the chat dock when a channel is selected (e.g., from HUD bubble click)
    setIsExpanded(true);
    
    setState(prev => ({ ...prev, channelId }));
    setOpenPicker(null);
    saveChannelPreference(panel, channelId);
    
    // Set highlight if we have a firstUnreadMessageId, otherwise clear it
    // This ensures each channel switch has a fresh highlight state
    setHighlightMessageId(prev => ({ ...prev, [panel]: firstUnreadMessageId || null }));
    
    // Refresh unread counts after a short delay to allow read cursor to update
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channel-unread-counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
    }, 1000);
  }, [saveChannelPreference, queryClient, topicChannels]);

  const handleHighlightComplete = useCallback((panel: 'left' | 'right') => {
    setHighlightMessageId(prev => ({ ...prev, [panel]: null }));
  }, []);

  const handleCreateDirectChannel = useCallback(async (panel: 'left' | 'right', memberId: number) => {
    try {
      const response = await apiRequest(
        'POST',
        `/api/chat/channels/direct/${memberId}`
      );
      
      const data = await response.json() as { channel: ChatChannel; created: boolean };
      const channelId = data.channel.id;
      
      // Select the channel in the panel
      const setState = panel === 'left' ? setLeftPanel : setRightPanel;
      setState(prev => ({ ...prev, channelId }));
      setOpenPicker(null);
      saveChannelPreference(panel, channelId);
      
      // Refresh channels list to include the new/existing direct channel
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
    } catch (error) {
      console.error('[ChatDock] Failed to create direct channel:', error);
    }
  }, [queryClient, saveChannelPreference]);

  const handlePickerTabChange = useCallback((panel: 'left' | 'right', tab: LeftPickerTab | RightPickerTab) => {
    const setState = panel === 'left' ? setLeftPanel : setRightPanel;
    setState(prev => ({ ...prev, pickerTab: tab }));
  }, []);

  const handleKeyDown = useCallback((panel: 'left' | 'right', e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey) {
        // Ctrl+Enter: insert a line break at cursor position
        e.preventDefault();
        const textarea = e.currentTarget;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = textarea.value;
        const newValue = currentValue.substring(0, start) + '\n' + currentValue.substring(end);
        handleInputChange(panel, newValue);
        // Set cursor position after the newline on next tick
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 1;
        });
      } else {
        // Enter without Ctrl: send message
        e.preventDefault();
        handleSend(panel);
      }
    }
  }, [handleSend, handleInputChange]);

  const truncateName = (name: string, maxLength: number): string => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const getChannelDisplayName = (channel: ChatChannel | undefined): string => {
    if (!channel) return 'Select Channel';
    if (channel.type === 'global') return 'Global Chat';
    if (channel.type === 'project') {
      const projectId = channel.projectId || 'Project';
      const projectName = (channel as any).projectName;
      if (projectName) {
        return `${projectId} - ${truncateName(projectName, 30)}`;
      }
      return projectId;
    }
    if (channel.type === 'client') {
      const projectId = channel.projectId || 'Project';
      const projectName = (channel as any).projectName;
      if (projectName) {
        return `${projectId} - ${truncateName(projectName, 22)} · Client`;
      }
      return `${projectId} · Client`;
    }
    if (channel.type === 'direct') {
      // Show only the other person's name, not "User1 & User2"
      const channelName = channel.name || 'Direct Message';
      if (channelName.includes(' & ') && user) {
        const parts = channelName.split(' & ');
        const currentUserName = user.firstName || user.username;
        // Return the name that's NOT the current user
        const otherName = parts.find(name => name !== currentUserName);
        if (otherName) return otherName;
      }
      return channelName;
    }
    return channel.name || 'Channel';
  };

  const formatTime = (dateStr: string) => {
    const date = ensureUTCDate(dateStr);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const last3Messages = (messages: ChatMessage[]) => messages.slice(-3);

  if (!user) return null;

  const leftChannel = channelsWithUnread.find(c => c.id === leftPanel.channelId)
    || (leftPanel.channelId ? clientChannelCache[leftPanel.channelId] : undefined);
  const rightChannel = channelsWithUnread.find(c => c.id === rightPanel.channelId) 
    || topicChannelsWithUnread.find(c => c.id === rightPanel.channelId);

  // Team ⇄ Client mode switch for the left panel (task #74): swaps between
  // a project's team channel and its client channel (get-or-create on demand).
  const handleToggleClientMode = async () => {
    if (!leftChannel?.projectId) return;
    try {
      if (leftChannel.type === 'project') {
        const response = await apiRequest('POST', '/api/chat/channels/client', { projectId: leftChannel.projectId });
        const clientChannel = await response.json() as ChatChannel;
        setClientChannelCache(prev => ({
          ...prev,
          [clientChannel.id]: { ...clientChannel, projectName: (leftChannel as any).projectName } as ChatChannel,
        }));
        queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
        handleChannelSelect('left', clientChannel.id);
      } else if (leftChannel.type === 'client') {
        const projectChannel = projectChannels.find(c => c.projectId === leftChannel.projectId);
        if (projectChannel) {
          handleChannelSelect('left', projectChannel.id);
        }
      }
    } catch (error) {
      console.error('[ChatDock] Failed to toggle client mode:', error);
    }
  };

  return (
    <motion.div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-card/80 backdrop-blur-xl backdrop-brightness-[0.8] border-t border-t-white",
        "transition-all duration-300"
      )}
      animate={{ height: isExpanded ? '50vh' : 93 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      data-testid="container-chat-dock"
    >
      {/* HUD Notification Bubbles - positioned absolutely above the dock */}
      {filteredProjectHud.length > 0 && (
        <HudBubbleContainer position="left" moreCount={Math.max(0, filteredProjectHud.length - 3)}>
          {filteredProjectHud.slice(0, 3).map((hud) => (
            <HudBubble
              key={hud.channelId}
              channelId={hud.channelId}
              displayText={hud.displayText}
              backgroundColor={hud.backgroundColor}
              unreadCount={hud.unreadCount}
              firstUnreadMessageId={hud.firstUnreadMessageId}
              onClick={(id, firstUnreadId) => handleChannelSelect('left', id, firstUnreadId)}
            />
          ))}
        </HudBubbleContainer>
      )}
      {filteredDockHud.length > 0 && (
        <HudBubbleContainer position="right" moreCount={Math.max(0, filteredDockHud.length - 3)}>
          {filteredDockHud.slice(0, 3).map((hud) => (
            <HudBubble
              key={hud.channelId}
              channelId={hud.channelId}
              displayText={hud.displayText}
              backgroundColor={hud.backgroundColor}
              unreadCount={hud.unreadCount}
              firstUnreadMessageId={hud.firstUnreadMessageId}
              onClick={(id, firstUnreadId) => handleChannelSelect('right', id, firstUnreadId)}
            />
          ))}
        </HudBubbleContainer>
      )}

      <div className="flex h-full">
        <ChatPanel
          panel="left"
          isExpanded={isExpanded}
          channel={leftChannel}
          messages={leftMessages}
          inputValue={leftPanel.inputValue}
          pickerTab={leftPanel.pickerTab as LeftPickerTab}
          onInputChange={(v) => handleInputChange('left', v)}
          onSend={() => handleSend('left')}
          onKeyDown={(e) => handleKeyDown('left', e)}
          onPickerOpen={() => setOpenPicker(openPicker === 'left' ? null : 'left')}
          pickerOpen={openPicker === 'left'}
          onPickerTabChange={(tab) => handlePickerTabChange('left', tab)}
          globalChannel={globalChannel}
          projectChannels={projectChannels}
          directChannels={directChannels}
          topicChannels={topicChannelsWithUnread}
          teamMembers={teamMembers}
          projects={allProjects}
          onChannelSelect={(id) => handleChannelSelect('left', id)}
          onCreateDirectChannel={(memberId) => handleCreateDirectChannel('left', memberId)}
          currentUserId={user.id}
          isAdmin={user.role === 'admin'}
          scrollRef={leftScrollRef}
          projectCallIntent={projectCallIntent}
          projectCallDataPending={isChannelsLoading || isTeamMembersLoading || isProjectsLoading}
          projectCallDataError={isChannelsError || isTeamMembersError || isProjectsError}
          projectRosterPending={isTeamMembersLoading || isProjectsLoading}
          projectRosterError={isTeamMembersError || isProjectsError}
          onProjectCallIntentConsumed={consumeProjectCallIntent}
          onProjectCallRecipientPickerNeeded={openProjectCallRecipientPicker}
          getChannelDisplayName={getChannelDisplayName}
          formatTime={formatTime}
          last3Messages={last3Messages}
          isSending={sendMessageMutation.isPending}
          onExpand={() => setIsExpanded(true)}
          onCollapse={() => setIsExpanded(false)}
          pendingAttachments={leftPanel.pendingAttachments}
          isDragging={leftPanel.isDragging}
          onDragOver={(e) => handleDragOver('left', e)}
          onDragLeave={(e) => handleDragLeave('left', e)}
          onDrop={(e) => handleDrop('left', e)}
          onPaste={(e) => handlePaste('left', e)}
          onAttachmentClick={() => fileInputLeftRef.current?.click()}
          fileInputRef={fileInputLeftRef}
          onFileInputChange={(e) => handleFileInputChange('left', e)}
          onRemoveAttachment={(id) => removePendingAttachment('left', id)}
          onJumpToMessage={(messageId) => handleJumpToMessage('left', messageId)}
          highlightMessageId={highlightMessageId.left}
          onHighlightComplete={() => handleHighlightComplete('left')}
          onSettingsClick={() => setSettingsOpen(true)}
          hasNewMessage={hasNewMessage.left}
          onClearHighlight={() => clearHighlight('left')}
          onPreviewAttachment={setPreviewAttachment}
          onLoadMore={handleLoadMoreLeft}
          hasMoreMessages={leftPagination.hasMore}
          isLoadingMore={leftPagination.isLoadingMore}
          replyToId={leftPanel.replyToId}
          replyToAuthor={leftPanel.replyToAuthor}
          onReply={(messageId, authorName) => setLeftPanel(prev => ({ ...prev, replyToId: messageId, replyToAuthor: authorName }))}
          onCancelReply={() => setLeftPanel(prev => ({ ...prev, replyToId: null, replyToAuthor: null }))}
          onScheduleSend={() => handleScheduleSend('left')}
          showScheduleButton={showScheduleButton}
          scheduleLabel={scheduleLabel}
          onDeleteMessage={(messageId, messageContent) => handleDeleteMessage(messageId, messageContent, leftChannel?.id || '')}
          delayedSend={delayedSend?.panel === 'left' ? delayedSend : null}
          onCancelDelayedSend={cancelDelayedSend}
          onToggleClientMode={handleToggleClientMode}
        />

        <div className="w-px bg-border" />

        <ChatPanel
          panel="right"
          isExpanded={isExpanded}
          channel={rightChannel}
          messages={rightMessages}
          inputValue={rightPanel.inputValue}
          pickerTab={rightPanel.pickerTab as RightPickerTab}
          onInputChange={(v) => handleInputChange('right', v)}
          onSend={() => handleSend('right')}
          onKeyDown={(e) => handleKeyDown('right', e)}
          onPickerOpen={() => setOpenPicker(openPicker === 'right' ? null : 'right')}
          pickerOpen={openPicker === 'right'}
          onPickerTabChange={(tab) => handlePickerTabChange('right', tab)}
          globalChannel={globalChannel}
          projectChannels={projectChannels}
          directChannels={directChannels}
          topicChannels={topicChannelsWithUnread}
          teamMembers={teamMembers}
          projects={allProjects}
          onChannelSelect={(id) => handleChannelSelect('right', id)}
          onCreateDirectChannel={(memberId) => handleCreateDirectChannel('right', memberId)}
          currentUserId={user.id}
          isAdmin={user.role === 'admin'}
          scrollRef={rightScrollRef}
          getChannelDisplayName={getChannelDisplayName}
          formatTime={formatTime}
          last3Messages={last3Messages}
          isSending={sendMessageMutation.isPending}
          onExpand={() => setIsExpanded(true)}
          onCollapse={() => setIsExpanded(false)}
          pendingAttachments={rightPanel.pendingAttachments}
          isDragging={rightPanel.isDragging}
          onDragOver={(e) => handleDragOver('right', e)}
          onDragLeave={(e) => handleDragLeave('right', e)}
          onDrop={(e) => handleDrop('right', e)}
          onPaste={(e) => handlePaste('right', e)}
          onAttachmentClick={() => fileInputRightRef.current?.click()}
          fileInputRef={fileInputRightRef}
          onFileInputChange={(e) => handleFileInputChange('right', e)}
          onRemoveAttachment={(id) => removePendingAttachment('right', id)}
          onJumpToMessage={(messageId) => handleJumpToMessage('right', messageId)}
          highlightMessageId={highlightMessageId.right}
          onHighlightComplete={() => handleHighlightComplete('right')}
          onSettingsClick={() => setSettingsOpen(true)}
          hasNewMessage={hasNewMessage.right}
          onClearHighlight={() => clearHighlight('right')}
          onPreviewAttachment={setPreviewAttachment}
          onLoadMore={handleLoadMoreRight}
          hasMoreMessages={rightPagination.hasMore}
          isLoadingMore={rightPagination.isLoadingMore}
          replyToId={rightPanel.replyToId}
          replyToAuthor={rightPanel.replyToAuthor}
          onReply={(messageId, authorName) => setRightPanel(prev => ({ ...prev, replyToId: messageId, replyToAuthor: authorName }))}
          onCancelReply={() => setRightPanel(prev => ({ ...prev, replyToId: null, replyToAuthor: null }))}
          onScheduleSend={() => handleScheduleSend('right')}
          showScheduleButton={showScheduleButton}
          scheduleLabel={scheduleLabel}
          onDeleteMessage={(messageId, messageContent) => handleDeleteMessage(messageId, messageContent, rightChannel?.id || '')}
          delayedSend={delayedSend?.panel === 'right' ? delayedSend : null}
          onCancelDelayedSend={cancelDelayedSend}
        />

        <div className="absolute right-2 top-2 flex items-center gap-1">
          {isExpanded && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setSettingsOpen(true)}
              data-testid="button-chat-settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid="button-expand-collapse"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <ChatDockSettingsDialog 
        open={settingsOpen} 
        onOpenChange={setSettingsOpen} 
      />

      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />

      {openPicker && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setOpenPicker(null)}
        />
      )}
    </motion.div>
  );
}

interface ChatPanelProps {
  panel: 'left' | 'right';
  isExpanded: boolean;
  channel: ChatChannel | undefined;
  messages: ChatMessage[];
  inputValue: string;
  pickerTab: LeftPickerTab | RightPickerTab;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPickerOpen: () => void;
  pickerOpen: boolean;
  onPickerTabChange: (tab: LeftPickerTab | RightPickerTab) => void;
  globalChannel: ChatChannel | undefined;
  projectChannels: ChatChannel[];
  directChannels: ChatChannel[];
  topicChannels: ChatChannel[];
  teamMembers: TeamMember[];
  projects: ProjectWithArtists[];
  onChannelSelect: (channelId: string) => void;
  onCreateDirectChannel: (memberId: number) => Promise<void>;
  currentUserId: number;
  isAdmin: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  /** Explicit shortcut request; only the left project picker receives one. */
  projectCallIntent?: ProjectCallIntent | null;
  projectCallDataPending?: boolean;
  projectCallDataError?: boolean;
  projectRosterPending?: boolean;
  projectRosterError?: boolean;
  onProjectCallIntentConsumed?: (requestId: number) => void;
  onProjectCallRecipientPickerNeeded?: () => void;
  getChannelDisplayName: (channel: ChatChannel | undefined) => string;
  formatTime: (dateStr: string) => string;
  last3Messages: (messages: ChatMessage[]) => ChatMessage[];
  isSending: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  pendingAttachments: PendingAttachment[];
  isDragging: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onAttachmentClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (id: string) => void;
  onJumpToMessage: (messageId: string) => void;
  highlightMessageId?: string | null;
  onHighlightComplete?: () => void;
  onSettingsClick?: () => void;
  hasNewMessage?: boolean;
  onClearHighlight?: () => void;
  onPreviewAttachment?: (attachment: PreviewAttachment) => void;
  onLoadMore?: () => void;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
  replyToId?: string | null;
  replyToAuthor?: string | null;
  onReply?: (messageId: string, authorName: string) => void;
  onCancelReply?: () => void;
  onScheduleSend?: () => void;
  showScheduleButton?: boolean;
  scheduleLabel?: string;
  onDeleteMessage?: (messageId: string, messageContent: string) => void;
  delayedSend?: DelayedSendState | null;
  onCancelDelayedSend?: () => void;
  onToggleClientMode?: () => void;
}

function ChatPanel({
  panel,
  isExpanded,
  channel,
  messages,
  inputValue,
  pickerTab,
  onInputChange,
  onSend,
  onKeyDown,
  onPickerOpen,
  pickerOpen,
  onPickerTabChange,
  globalChannel,
  projectChannels,
  directChannels,
  topicChannels,
  teamMembers,
  projects,
  onChannelSelect,
  onCreateDirectChannel,
  currentUserId,
  isAdmin,
  scrollRef,
  projectCallIntent,
  projectCallDataPending,
  projectCallDataError,
  projectRosterPending,
  projectRosterError,
  onProjectCallIntentConsumed,
  onProjectCallRecipientPickerNeeded,
  getChannelDisplayName,
  formatTime,
  last3Messages,
  isSending,
  onExpand,
  onCollapse,
  pendingAttachments,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onPaste,
  onAttachmentClick,
  fileInputRef,
  onFileInputChange,
  onRemoveAttachment,
  onJumpToMessage,
  highlightMessageId,
  onHighlightComplete,
  onSettingsClick,
  hasNewMessage,
  onClearHighlight,
  onPreviewAttachment,
  onLoadMore,
  hasMoreMessages,
  isLoadingMore,
  replyToId,
  replyToAuthor,
  onReply,
  onCancelReply,
  onScheduleSend,
  showScheduleButton,
  scheduleLabel,
  onDeleteMessage,
  delayedSend,
  onCancelDelayedSend,
  onToggleClientMode,
}: ChatPanelProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [scheduledPopoverOpen, setScheduledPopoverOpen] = useState(false);
  const { startCall, pendingOutgoing, activeCall } = useCall();
  const previewMessages = last3Messages(messages);

  const headerProject = useMemo(
    () => channel?.type === 'project' && channel.projectId
      ? projects.find((project) => project.id === channel.projectId)
      : undefined,
    [channel?.projectId, channel?.type, projects],
  );
  
  const isDelayActive = !!delayedSend;
  const [delayCountdown, setDelayCountdown] = useState(5);

  useEffect(() => {
    if (!delayedSend) {
      setDelayCountdown(5);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - delayedSend.startedAt) / 1000;
      const remaining = Math.max(0, Math.ceil(5 - elapsed));
      setDelayCountdown(remaining);
    };
    tick();
    const interval = setInterval(tick, 100);
    return () => clearInterval(interval);
  }, [delayedSend]);

  const isUnsubscribed = channel?.type === 'project' && !projectChannels.some(c => c.id === channel.id);
  const panelQueryClient = useQueryClient();
  
  const channelIdForScheduled = channel?.id;
  const { data: scheduledMessages = [] } = useQuery<ScheduledMessageStatus[]>({
    queryKey: ['/api/chat/channels', channelIdForScheduled, 'messages', 'scheduled'],
    // Apply to cached responses and refreshes alike; both composer layouts use
    // this same projection for the clock, badge count, and status popover.
    select: outstandingScheduledMessages,
    // Existing queued messages remain relevant during office hours, even
    // though scheduling a NEW message is only offered outside those hours.
    enabled: !!channelIdForScheduled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
  
  const cancelScheduledMutation = useMutation({
    mutationFn: async (messageId: number) => {
      const response = await apiRequest('DELETE', `/api/chat/messages/scheduled/${messageId}`);
      return response.json();
    },
    onSettled: () => {
      panelQueryClient.invalidateQueries({
        queryKey: ['/api/chat/channels', channelIdForScheduled, 'messages', 'scheduled']
      });
    },
  });
  
  // Create mentionUserMap for rendering @shortCode as @firstName
  const mentionUserMap = useMemo(() => {
    const map = new Map<string, string>();
    teamMembers.forEach(member => {
      if (member.shortCode && member.firstName) {
        map.set(member.shortCode.toUpperCase(), member.firstName);
        map.set(member.shortCode, member.firstName);
      }
    });
    return map;
  }, [teamMembers]);
  
  // @mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Fetch channel members for filtering @mentions
  interface ChannelMember {
    id: string;
    channelId: string;
    userId: number;
    role: string;
    user?: {
      id: number;
      firstName?: string | null;
      lastName?: string | null;
      shortCode?: string | null;
      colorCode?: string | null;
    };
  }
  
  const { data: channelMembers = [] } = useQuery<ChannelMember[]>({
    queryKey: ['/api/chat/channels', channel?.id, 'members'],
    enabled: !!channel?.id,
    staleTime: 60000,
  });
  
  // Get mentionable users based on channel type
  const mentionableUsers = useMemo(() => {
    if (!channel) return [];
    
    // For direct channels: only show the other person (not yourself)
    if (channel.type === 'direct') {
      const otherMember = channelMembers.find(m => m.userId !== currentUserId);
      if (otherMember?.user) {
        const teamMember = teamMembers.find(t => t.id === otherMember.userId);
        if (teamMember) return [teamMember];
      }
      // Fallback: filter teamMembers by channel members
      return teamMembers.filter(t => 
        channelMembers.some(m => m.userId === t.id && m.userId !== currentUserId)
      );
    }
    
    // For project/global/topic channels: all team members (excluding self)
    return teamMembers.filter(t => t.id !== currentUserId);
  }, [channel, channelMembers, teamMembers, currentUserId]);
  
  // Filter mentionable users based on search (uses shortCode for @mentions)
  const filteredMentions = useMemo(() => {
    if (!showMentions || !mentionSearch) return mentionableUsers.slice(0, 8);
    const search = mentionSearch.toLowerCase();
    return mentionableUsers
      .filter(m => 
        (m.shortCode && m.shortCode.toLowerCase().includes(search)) ||
        (m.firstName && m.firstName.toLowerCase().includes(search)) ||
        (m.lastName && m.lastName.toLowerCase().includes(search))
      )
      .slice(0, 8);
  }, [mentionableUsers, mentionSearch, showMentions]);
  
  // Build message tree for threaded display
  const messageTree = useMemo(() => {
    return buildMessageTree(messages);
  }, [messages]);
  
  // Handle input change for @mention detection
  const handleInputChangeWithMention = useCallback((value: string, cursorPos?: number) => {
    onInputChange(value);
    // Clear new message highlight when user starts typing
    onClearHighlight?.();
    
    // Get cursor position from textarea
    const pos = cursorPos ?? textareaRef.current?.selectionStart ?? value.length;
    
    // Check for @ mention trigger
    const textBeforeCursor = value.slice(0, pos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex >= 0) {
      // Check if there's a space before @ (or @ is at start)
      const charBefore = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || lastAtIndex === 0) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        // Only show suggestions if no space after @
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setShowMentions(true);
          setMentionSearch(textAfterAt);
          setMentionStartPos(lastAtIndex);
          setMentionIndex(0);
          return;
        }
      }
    }
    
    setShowMentions(false);
    setMentionSearch('');
  }, [onInputChange]);
  
  // Insert selected mention (uses firstName for display, shortCode stored in message for server parsing)
  const insertMention = useCallback((member: TeamMember) => {
    // Use firstName for display in input field
    const displayName = member.firstName || member.shortCode || `user${member.id}`;
    const beforeMention = inputValue.slice(0, mentionStartPos);
    const afterMention = inputValue.slice(mentionStartPos + 1 + mentionSearch.length);
    const newValue = `${beforeMention}@${displayName} ${afterMention}`;
    onInputChange(newValue);
    setShowMentions(false);
    setMentionSearch('');
    
    // Focus textarea and set cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartPos + displayName.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [inputValue, mentionStartPos, mentionSearch, onInputChange]);
  
  const insertEmoji = useCallback((emoji: string) => {
    const cursorPos = textareaRef.current?.selectionStart ?? inputValue.length;
    const newValue = inputValue.slice(0, cursorPos) + emoji + inputValue.slice(cursorPos);
    onInputChange(newValue);
    setEmojiPickerOpen(false);
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = cursorPos + emoji.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [inputValue, onInputChange]);
  
  // Handle keyboard navigation in mention popup
  // Enter-to-send is preserved - only Tab inserts mentions
  const handleMentionKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => (prev + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => (prev - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      // Tab inserts the selected mention - Enter still sends message
      if (e.key === 'Tab') {
        if (filteredMentions[mentionIndex]) {
          e.preventDefault();
          insertMention(filteredMentions[mentionIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    
    // Pass through to original handler (Enter sends message)
    onKeyDown(e);
  }, [showMentions, filteredMentions, mentionIndex, insertMention, onKeyDown]);
  
  const getFirstName = (fullName: string | undefined | null): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Get display name from author object (external client authors have no user row)
  const getAuthorDisplayName = (author?: ChatMessageAuthor | null, msg?: ChatMessage): string => {
    if (!author) {
      if (msg?.externalAuthorKind === 'client') return msg.externalAuthorName || 'Kunde';
      return 'Unknown';
    }
    if (author.firstName) return author.firstName;
    return author.username;
  };

  const isNearBottomRef = useRef(true);
  const isAutoScrollingRef = useRef(false);
  
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      isAutoScrollingRef.current = true;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      isAutoScrollingRef.current = false;
    }
  }, [scrollRef]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 100;
      if (!isAutoScrollingRef.current && hasNewMessage) {
        onClearHighlight?.();
      }
      // Trigger load more when near the top
      if (scrollTop < 100 && onLoadMore && hasMoreMessages && !isLoadingMore) {
        onLoadMore();
      }
    }
  }, [scrollRef, onClearHighlight, onLoadMore, hasMoreMessages, isLoadingMore, hasNewMessage]);

  useLayoutEffect(() => {
    if (highlightMessageId) return;
    if (isExpanded) {
      scrollToBottom();
    }
  }, [isExpanded, scrollToBottom, highlightMessageId]);

  useLayoutEffect(() => {
    if (highlightMessageId) return;
    if (isExpanded && messages.length > 0 && isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, isExpanded, scrollToBottom, highlightMessageId]);

  useLayoutEffect(() => {
    if (highlightMessageId) return;
    if (channel?.id) {
      isNearBottomRef.current = true;
      scrollToBottom();
    }
  }, [channel?.id, scrollToBottom, highlightMessageId]);

  // Handle jump-to-unread with DOM existence check
  // Note: ChatMessageBubble handles the actual scrollIntoView when highlighted.
  // This effect only handles the fallback case when target is not in DOM.
  useEffect(() => {
    if (highlightMessageId && scrollRef.current) {
      // Wait a tick for messages to render
      requestAnimationFrame(() => {
        const targetElement = document.getElementById(`message-${highlightMessageId}`);
        if (!targetElement && scrollRef.current) {
          // Target not in DOM - scroll to top to indicate "older messages are up here"
          scrollRef.current.scrollTop = 0;
        }
        // If target exists, ChatMessageBubble's useEffect handles scrollIntoView
      });
    }
  }, [highlightMessageId, scrollRef]);

  useEffect(() => {
    if (highlightMessageId) {
      const safetyTimer = setTimeout(() => {
        onHighlightComplete?.();
      }, 4000);
      return () => clearTimeout(safetyTimer);
    }
  }, [highlightMessageId, onHighlightComplete]);

  // Mark channel as read when messages are viewed
  // Uses lastReadMessageIdRef to prevent duplicate API calls
  const lastReadMessageIdRef = useRef<string | null>(null);
  const prevChannelIdRef = useRef<string | null>(null);
  
  // Reset lastReadMessageIdRef when channel changes to allow new mark-read calls
  useEffect(() => {
    if (channel?.id && channel.id !== prevChannelIdRef.current) {
      lastReadMessageIdRef.current = null;
      prevChannelIdRef.current = channel.id;
    }
  }, [channel?.id]);
  
  useEffect(() => {
    // A selected channel is not being read while the dock is minimized.
    // Preserve unread counts (including urgent mentions) until it is expanded.
    if (!isExpanded || !channel?.id || messages.length === 0) return;
    
    // Find the latest non-optimistic message ID. Optimistic placeholders use ids
    // like `optimistic-<uuid>` which exceed the 36-char varchar on the server's
    // read-cursor column and would crash the mark-read write. Skip until the
    // server has echoed back a real message id.
    let latestMessage: ChatMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const candidate = messages[i];
      if (!candidate?.id) continue;
      if (candidate._optimistic) continue;
      if (candidate.id.startsWith('optimistic-')) continue;
      latestMessage = candidate;
      break;
    }
    if (!latestMessage?.id) return;
    
    // Skip if we already marked this message as read
    if (lastReadMessageIdRef.current === latestMessage.id) return;
    
    // Mark the channel as read with the latest message ID
    // The backend advances by message chronology to prevent stale cursor overwrites.
    apiRequest('POST', `/api/chat/channels/${channel.id}/mark-read`, { messageId: latestMessage.id })
      .then(() => {
        lastReadMessageIdRef.current = latestMessage!.id;
        panelQueryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
        panelQueryClient.invalidateQueries({ queryKey: ['/api/chat/channel-unread-counts'] });
        panelQueryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
      })
      .catch((error) => {
        console.error('[ChatPanel] Failed to mark channel as read:', error);
      });
  }, [isExpanded, channel?.id, messages, panelQueryClient]);

  const getRightPanelTint = () => {
    // Client-Chat mode (task #74): messages here reach the client — make
    // the whole panel unmistakably yellow.
    if (channel?.type === 'client') return 'bg-yellow-500/15';
    if (panel !== 'right') return '';
    if (channel?.type === 'global') return 'bg-emerald-500/10';
    if (channel?.type === 'topic') return 'bg-amber-500/10';
    return '';
  };

  if (isExpanded) {
    return (
      <div className={cn(
        "flex-1 flex flex-col relative px-2 py-1 min-h-0 min-w-0 w-1/2",
        getRightPanelTint()
      )}>
        <div 
          className={cn(
            "flex items-center gap-2 py-1.5 border-b shrink-0 -mx-2 px-2",
            hasNewMessage 
              ? "animate-new-message-pulse border-primary/60" 
              : "border-border/50"
          )}
          style={{ backgroundColor: hasNewMessage ? undefined : '#1c202c' }}
          data-testid={`header-collapse-${panel}`}
        >
          <div 
            className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-accent/30 rounded transition-colors px-1"
            onClick={onCollapse}
          >
            <Hash className={cn("h-3 w-3", hasNewMessage ? "text-white" : "text-primary")} />
            <span
              className={cn(
                "text-xs font-medium truncate",
                hasNewMessage ? "text-white" : "text-foreground",
                channel?.type === 'project' && channel?.projectId && "hover:text-blue-400 hover:underline cursor-pointer"
              )}
              onClick={(e) => {
                if (channel?.type === 'project' && channel?.projectId) {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('navigate-to-project', {
                    detail: { projectId: channel.projectId }
                  }));
                }
              }}
            >
              {getChannelDisplayName(channel)}
            </span>
            {channel?.type === 'project' && channelMembers.length > 0 && (
              <div className="flex items-center gap-0.5 shrink-0 overflow-hidden">
                {channelMembers.map((member) => {
                  const shortCode = member.user?.shortCode;
                  const color = getUserColor(member.user?.colorCode);
                  if (!shortCode) return null;
                  return (
                    <span
                      key={member.id}
                      className="text-[10px] font-bold px-1 py-0.5 rounded text-white whitespace-nowrap"
                      style={{ backgroundColor: color, textShadow: '0px 1px 2px rgba(0,0,0,0.8)' }}
                      title={[member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') || shortCode}
                    >
                      {shortCode}
                    </span>
                  );
                })}
              </div>
            )}
            {(channel?.unreadCount ?? 0) > 0 && (
              <span className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full",
                channel?.type === 'client' ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground"
              )}>
                {channel?.unreadCount}
              </span>
            )}
          </div>
          {panel === 'left' && channel?.type === 'project' && channel.projectId && (
            <ProjectHeaderVideoCall
              channel={{
                id: channel.id,
                type: channel.type,
                projectId: channel.projectId,
              }}
              project={headerProject}
              teamMembers={teamMembers}
              currentUserId={currentUserId}
              rosterPending={projectRosterPending ?? false}
              rosterError={projectRosterError ?? false}
              callBusy={!!pendingOutgoing || !!activeCall}
              startCall={startCall}
            />
          )}
          {onToggleClientMode && (channel?.type === 'project' || channel?.type === 'client') && (
            <div
              className="flex items-center rounded-md overflow-hidden border border-border/60 shrink-0"
              onClick={(e) => e.stopPropagation()}
              data-testid={`toggle-client-mode-${panel}`}
            >
              <button
                className={cn(
                  "px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                  channel?.type === 'project'
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => channel?.type !== 'project' && onToggleClientMode()}
                data-testid={`button-team-mode-${panel}`}
              >
                Team
              </button>
              <button
                className={cn(
                  "px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                  channel?.type === 'client'
                    ? "bg-yellow-500 text-black"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => channel?.type !== 'client' && onToggleClientMode()}
                data-testid={`button-client-mode-${panel}`}
              >
                Client
              </button>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setIsSearchOpen(!isSearchOpen);
            }}
            data-testid={`search-toggle-${panel}`}
          >
            <Search className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onSettingsClick?.();
            }}
            data-testid={`settings-toggle-${panel}`}
          >
            <Settings className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={onCollapse}
            data-testid={`collapse-${panel}`}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>

        {isSearchOpen && channel && (
          <ChatSearch
            channelId={channel.id}
            onJumpToMessage={onJumpToMessage}
            onClose={() => setIsSearchOpen(false)}
            className="absolute top-9 left-2 right-2 z-10"
          />
        )}

        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          onClick={() => onClearHighlight?.()}
          className="flex-1 overflow-y-auto py-2 min-h-0"
          style={{ overflowAnchor: 'none' }}
          data-testid={`container-messages-${panel}`}
        >
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
              No messages yet
            </div>
          ) : (
            <div className="space-y-2">
              {isLoadingMore && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {messageTree.roots.map((group, index, arr) => {
                const prevGroup = index > 0 ? arr[index - 1] : null;
                const isSameAuthor = prevGroup && prevGroup.authorId === group.authorId;
                const withinFiveMinutes = prevGroup && (() => {
                  const prevTime = ensureUTCDate(prevGroup.createdAt).getTime();
                  const currTime = ensureUTCDate(group.createdAt).getTime();
                  return (currTime - prevTime) < 5 * 60 * 1000;
                })();
                const showRootAuthor = !isSameAuthor || !withinFiveMinutes;
                
                return (
                <ThreadedMessageGroup
                  key={group.id}
                  messageGroup={group as any}
                  currentUserId={currentUserId}
                  channelId={channel?.id || ''}
                  channelType={channel?.type}
                  highlightMessageId={highlightMessageId}
                  onHighlightComplete={onHighlightComplete}
                  mentionUserMap={mentionUserMap}
                  onPreviewAttachment={onPreviewAttachment}
                  onReply={onReply}
                  showRootAuthor={showRootAuthor}
                  renderMessage={(props) => (
                    <ChatMessageBubble
                      message={props.message as any}
                      isOwn={props.isOwn}
                      showAuthor={props.showAuthor}
                      currentUserId={props.currentUserId}
                      channelId={props.channelId}
                      channelType={props.channelType}
                      compact={props.compact}
                      highlightMessageId={props.highlightMessageId}
                      onHighlightComplete={props.onHighlightComplete}
                      mentionUserMap={props.mentionUserMap}
                      onPreviewAttachment={props.onPreviewAttachment}
                      isReply={props.isReply}
                      onReply={props.onReply}
                      isAdmin={isAdmin}
                      onDelete={onDeleteMessage}
                    />
                  )}
                />
              );
              })}
            </div>
          )}
        </div>

        {replyToId && replyToAuthor && onCancelReply && (
          <div className="flex items-center gap-2 px-2 py-1 border-t border-border/50 bg-muted/30 shrink-0">
            <Reply className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs text-muted-foreground">
              Replying to <span className="font-medium text-foreground">{replyToAuthor}</span>
            </span>
            <button
              onClick={onCancelReply}
              className="ml-auto h-5 w-5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Cancel reply"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1 py-1 border-t border-border/50 shrink-0">
            {pendingAttachments.map((att) => {
              const FileIconComponent = getFileIcon(att.mimeType, att.fileExtension);
              return (
                <div 
                  key={att.id} 
                  className="relative group"
                >
                  {att.isImage && att.previewUrl ? (
                    <img 
                      src={att.previewUrl} 
                      alt="Preview" 
                      className={cn(
                        "h-12 w-12 object-cover rounded",
                        att.status === 'uploading' && "opacity-50"
                      )}
                    />
                  ) : (
                    <div className={cn(
                      "h-12 min-w-[80px] px-2 flex flex-col items-center justify-center rounded bg-muted/80 border border-border",
                      att.status === 'uploading' && "opacity-50"
                    )}>
                      <FileIconComponent className="h-5 w-5 text-muted-foreground" />
                      <span className="text-[9px] font-medium text-muted-foreground mt-0.5 truncate max-w-[70px]">
                        {att.fileExtension}
                      </span>
                      <span className="text-[8px] text-muted-foreground/70">
                        {att.fileSize}
                      </span>
                    </div>
                  )}
                  {att.status === 'uploading' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded bg-background/60">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-[9px] font-semibold text-foreground tabular-nums">
                        {att.progress}%
                      </span>
                    </div>
                  )}
                  {att.status === 'error' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-destructive/50 rounded">
                      <X className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(att.id)}
                    className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {channel?.type === 'client' && (
          <div
            className="flex items-center gap-1.5 -mx-2 px-3 py-1 bg-yellow-500/20 border-t border-yellow-500/40 text-[11px] shrink-0"
            data-testid={`client-mode-notice-${panel}`}
          >
            <span className="font-semibold text-yellow-300">Client-Chat</span>
            <span className="text-yellow-200/80">— Nachrichten hier gehen an den Kunden</span>
          </div>
        )}
        <div 
          className={cn(
            "flex items-center gap-1.5 py-1 border-t border-border/50 relative shrink-0",
            channel?.type === 'client' && "border-yellow-500/40",
            isDragging && "ring-2 ring-primary ring-inset bg-primary/10"
          )}
          onDragOver={isUnsubscribed ? undefined : onDragOver}
          onDragLeave={isUnsubscribed ? undefined : onDragLeave}
          onDrop={isUnsubscribed ? undefined : onDrop}
        >
          {isDragging && !isUnsubscribed && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-2 text-primary text-xs font-medium">
                <Paperclip className="h-4 w-4" />
                Drop file here
              </div>
            </div>
          )}
          <ChannelPickerButton
            panel={panel}
            channel={channel}
            pickerOpen={pickerOpen}
            pickerTab={pickerTab}
            onPickerOpen={onPickerOpen}
            onPickerTabChange={onPickerTabChange}
            globalChannel={globalChannel}
            projectChannels={projectChannels}
            directChannels={directChannels}
            topicChannels={topicChannels}
            teamMembers={teamMembers}
            projects={projects}
            onChannelSelect={onChannelSelect}
            onCreateDirectChannel={onCreateDirectChannel}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            projectCallIntent={projectCallIntent}
            projectCallDataPending={projectCallDataPending}
            projectCallDataError={projectCallDataError}
            onProjectCallIntentConsumed={onProjectCallIntentConsumed}
            onProjectCallRecipientPickerNeeded={onProjectCallRecipientPickerNeeded}
            getChannelDisplayName={getChannelDisplayName}
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onFileInputChange}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onAttachmentClick}
            title="Attach file"
            disabled={isUnsubscribed}
          >
            <Paperclip className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </Button>

          <div className={cn("relative flex-1", isUnsubscribed && "opacity-60")}>
            {isDelayActive ? (
              <div
                className="w-full h-7 text-xs bg-gray-300 text-gray-500 border-0 rounded-md px-3 py-1.5 overflow-hidden truncate select-none"
                data-testid={`input-message-${panel}-delayed`}
                title={delayedSend.content.replace(/<[^>]*>/g, '')}
              >
                {delayedSend.content.replace(/<[^>]*>/g, '')}
              </div>
            ) : (
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => handleInputChangeWithMention(e.target.value, e.target.selectionStart)}
              onKeyDown={handleMentionKeyDown}
              onPaste={isUnsubscribed ? undefined : onPaste}
              disabled={isUnsubscribed}
              placeholder={isUnsubscribed ? "You are not subscribed to this project chat." : isDragging ? "" : "Type a message or drop files... (@ to mention)"}
              className={cn(
                "w-full h-7 text-xs bg-gray-100 text-black border-0 placeholder:text-gray-500 rounded-md px-3 py-1.5 resize-none overflow-hidden",
                isDragging && "opacity-0"
              )}
              rows={1}
              data-testid={`input-message-${panel}`}
            />
            )}
            {showMentions && filteredMentions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 w-64 max-h-48 overflow-y-auto bg-popover border border-border rounded-md shadow-lg z-50">
                {filteredMentions.map((member, idx) => (
                  <div
                    key={member.id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs",
                      idx === mentionIndex ? "bg-accent" : "hover:bg-accent/50"
                    )}
                    onClick={() => insertMention(member)}
                  >
                    <div className="relative shrink-0">
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ backgroundColor: getUserColor(member.colorCode) }}
                      >
                        {member.firstName?.[0] || member.shortCode?.[0] || '?'}
                      </div>
                      <StatusBubble userId={member.id} size="sm" showTooltip={false} className="absolute -bottom-0.5 -right-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {member.firstName && member.lastName 
                          ? `${member.firstName} ${member.lastName}`
                          : member.shortCode || `User ${member.id}`}
                      </div>
                      <div className="text-muted-foreground text-[10px] truncate">@{member.shortCode || `user${member.id}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                title="Insert emoji"
              >
                <Smile className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" side="top" align="end" sideOffset={4}>
              <div className="grid grid-cols-5 gap-1">
                {CHAT_EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    className="text-lg hover:scale-125 transition-transform p-1 rounded hover:bg-muted"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {((showScheduleButton && onScheduleSend) || scheduledMessages.length > 0) && (
            <div className="relative flex items-center">
              {showScheduleButton && onScheduleSend ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onScheduleSend}
                disabled={isUnsubscribed || (!inputValue.trim() && pendingAttachments.filter(a => a.status === 'completed').length === 0) || isSending || pendingAttachments.some(a => a.status === 'uploading')}
                title={scheduleLabel ? `Send on ${scheduleLabel}` : 'Schedule send'}
                data-testid={`button-schedule-send-${panel}`}
              >
                <Clock className={cn(
                  "h-3.5 w-3.5",
                  (inputValue.trim() || pendingAttachments.filter(a => a.status === 'completed').length > 0) ? "text-amber-500" : "text-muted-foreground opacity-50"
                )} />
              </Button>
              ) : (
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  title="View scheduled messages" aria-label="View scheduled messages"
                  onClick={() => setScheduledPopoverOpen(true)}>
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                </Button>
              )}
              {scheduledMessages.length > 0 && (
                <Popover open={scheduledPopoverOpen} onOpenChange={setScheduledPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 cursor-pointer hover:bg-amber-400 transition-colors"
                      title={`${scheduledMessages.length} scheduled message statuses`}
                      aria-label="View scheduled message statuses"
                    >
                      {scheduledMessages.length}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-2" side="top" align="end" sideOffset={8}>
                    <ScheduledMessageList messages={scheduledMessages}
                      onCancel={id => cancelScheduledMutation.mutate(id)}
                      isCancelling={cancelScheduledMutation.isPending}
                      cancellationError={cancelScheduledMutation.isError
                        ? 'Cancellation could not be confirmed. Check the refreshed status and the chat before sending again.'
                        : undefined} />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}

          {isDelayActive ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative group"
              onClick={onCancelDelayedSend}
              title="Cancel send"
              data-testid={`button-cancel-delayed-${panel}`}
            >
              <svg className="absolute inset-0 w-7 h-7" viewBox="0 0 28 28">
                <circle
                  cx="14"
                  cy="14"
                  r="11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-red-500/30"
                />
                <circle
                  cx="14"
                  cy="14"
                  r="11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-red-500"
                  strokeDasharray={`${2 * Math.PI * 11}`}
                  strokeDashoffset={`${2 * Math.PI * 11 * (1 - delayCountdown / 5)}`}
                  transform="rotate(-90 14 14)"
                  style={{ transition: 'stroke-dashoffset 0.15s linear' }}
                />
              </svg>
              <X className="h-3 w-3 text-red-500 relative z-10" />
            </Button>
          ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onSend}
            disabled={isUnsubscribed || (!inputValue.trim() && pendingAttachments.filter(a => a.status === 'completed').length === 0) || isSending || pendingAttachments.some(a => a.status === 'uploading')}
            data-testid={`button-send-${panel}`}
          >
            <Send className={cn(
              "h-3.5 w-3.5",
              (inputValue.trim() || pendingAttachments.filter(a => a.status === 'completed').length > 0) ? "text-primary" : "text-muted-foreground opacity-50"
            )} />
          </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex-1 flex flex-col relative px-2 py-1 h-[93px] min-h-0 min-w-0 w-1/2",
      getRightPanelTint(),
      hasNewMessage && "animate-new-message-pulse"
    )}>
      <div 
        className="flex-1 min-h-0 overflow-hidden flex flex-col justify-end text-xs cursor-pointer hover:bg-accent/30 rounded transition-colors"
        data-testid={`preview-messages-${panel}`}
        onClick={() => {
          onClearHighlight?.();
          onExpand();
        }}
      >
        {previewMessages.length === 0 ? (
          <div className="text-muted-foreground italic text-center py-1">
            {channel ? 'No messages' : 'Select a channel'}
          </div>
        ) : (
          <div className="space-y-0.5">
            {previewMessages.map((msg) => (
              <div key={msg.id} className="truncate leading-tight">
                <span 
                  className="font-semibold"
                  style={msg.authorId !== currentUserId && msg.author?.colorCode ? { color: msg.author.colorCode } : undefined}
                >
                  {getAuthorDisplayName(msg.author, msg)}:
                </span>{' '}
                <span className="text-foreground">{msg.content}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border/50 pt-1 shrink-0">
        <ChannelPickerButton
          panel={panel}
          channel={channel}
          pickerOpen={pickerOpen}
          pickerTab={pickerTab}
          onPickerOpen={onPickerOpen}
          onPickerTabChange={onPickerTabChange}
          globalChannel={globalChannel}
          projectChannels={projectChannels}
          directChannels={directChannels}
          topicChannels={topicChannels}
          teamMembers={teamMembers}
          projects={projects}
          onChannelSelect={onChannelSelect}
          onCreateDirectChannel={onCreateDirectChannel}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          getChannelDisplayName={getChannelDisplayName}
        />

        <div className={cn("relative flex-1", isUnsubscribed && "opacity-60")}>
          {isDelayActive ? (
            <div
              className="w-full h-7 text-xs bg-gray-300 text-gray-500 border-0 rounded-md px-3 py-1.5 overflow-hidden truncate select-none"
              data-testid={`input-message-${panel}-delayed`}
              title={delayedSend.content.replace(/<[^>]*>/g, '')}
            >
              {delayedSend.content.replace(/<[^>]*>/g, '')}
            </div>
          ) : (
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => handleInputChangeWithMention(e.target.value, e.target.selectionStart)}
            onKeyDown={handleMentionKeyDown}
            onPaste={isUnsubscribed ? undefined : onPaste}
            disabled={isUnsubscribed}
            placeholder={isUnsubscribed ? "You are not subscribed to this project chat." : "Type a message... (@ to mention)"}
            className="w-full h-7 text-xs bg-gray-100 text-black border-0 placeholder:text-gray-500 rounded-md px-3 py-1.5 resize-none overflow-hidden"
            rows={1}
            data-testid={`input-message-${panel}`}
          />
          )}
          {showMentions && filteredMentions.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-64 max-h-48 overflow-y-auto bg-popover border border-border rounded-md shadow-lg z-50">
              {filteredMentions.map((member, idx) => (
                <div
                  key={member.id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs",
                    idx === mentionIndex ? "bg-accent" : "hover:bg-accent/50"
                  )}
                  onClick={() => insertMention(member)}
                >
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                    style={{ backgroundColor: getUserColor(member.colorCode) }}
                  >
                    {member.firstName?.[0] || member.shortCode?.[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {member.firstName && member.lastName 
                        ? `${member.firstName} ${member.lastName}`
                        : member.shortCode || `User ${member.id}`}
                    </div>
                    <div className="text-muted-foreground text-[10px] truncate">@{member.shortCode || `user${member.id}`}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {((showScheduleButton && onScheduleSend) || scheduledMessages.length > 0) && (
          <div className="relative flex items-center">
            {showScheduleButton && onScheduleSend ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onScheduleSend}
              disabled={isUnsubscribed || !inputValue.trim() || isSending}
              title={scheduleLabel ? `Send on ${scheduleLabel}` : 'Schedule send'}
              data-testid={`button-schedule-send-collapsed-${panel}`}
            >
              <Clock className={cn(
                "h-3.5 w-3.5",
                inputValue.trim() ? "text-amber-500" : "text-muted-foreground opacity-50"
              )} />
            </Button>
            ) : (
              <Button variant="ghost" size="icon" className="h-7 w-7"
                title="View scheduled messages" aria-label="View scheduled messages"
                onClick={() => { setScheduledPopoverOpen(true); onExpand(); }}>
                <Clock className="h-3.5 w-3.5 text-amber-500" />
              </Button>
            )}
            {scheduledMessages.length > 0 && (
              <button type="button" title={`${scheduledMessages.length} scheduled messages`}
                onClick={() => { setScheduledPopoverOpen(true); onExpand(); }}
                className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                {scheduledMessages.length}
              </button>
            )}
          </div>
        )}

        {isDelayActive ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 relative group"
            onClick={onCancelDelayedSend}
            title="Cancel send"
            data-testid={`button-cancel-delayed-${panel}`}
          >
            <svg className="absolute inset-0 w-7 h-7" viewBox="0 0 28 28">
              <circle
                cx="14"
                cy="14"
                r="11"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-red-500/30"
              />
              <circle
                cx="14"
                cy="14"
                r="11"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-red-500"
                strokeDasharray={`${2 * Math.PI * 11}`}
                strokeDashoffset={`${2 * Math.PI * 11 * (1 - delayCountdown / 5)}`}
                transform="rotate(-90 14 14)"
                style={{ transition: 'stroke-dashoffset 0.15s linear' }}
              />
            </svg>
            <X className="h-3 w-3 text-red-500 relative z-10" />
          </Button>
        ) : (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onSend}
          disabled={isUnsubscribed || !inputValue.trim() || isSending}
          data-testid={`button-send-${panel}`}
        >
          <Send className={cn(
            "h-3.5 w-3.5",
            inputValue.trim() ? "text-primary" : "text-muted-foreground opacity-50"
          )} />
        </Button>
        )}
      </div>
    </div>
  );
}

interface ChannelPickerButtonProps {
  panel: 'left' | 'right';
  channel: ChatChannel | undefined;
  pickerOpen: boolean;
  pickerTab: LeftPickerTab | RightPickerTab;
  onPickerOpen: () => void;
  onPickerTabChange: (tab: LeftPickerTab | RightPickerTab) => void;
  globalChannel: ChatChannel | undefined;
  projectChannels: ChatChannel[];
  directChannels: ChatChannel[];
  topicChannels: ChatChannel[];
  teamMembers: TeamMember[];
  projects: ProjectWithArtists[];
  onChannelSelect: (channelId: string) => void;
  onCreateDirectChannel: (memberId: number) => Promise<void>;
  currentUserId: number;
  isAdmin: boolean;
  projectCallIntent?: ProjectCallIntent | null;
  projectCallDataPending?: boolean;
  projectCallDataError?: boolean;
  onProjectCallIntentConsumed?: (requestId: number) => void;
  onProjectCallRecipientPickerNeeded?: () => void;
  getChannelDisplayName: (channel: ChatChannel | undefined) => string;
}

function ChannelPickerButton({
  panel,
  channel,
  pickerOpen,
  pickerTab,
  onPickerOpen,
  onPickerTabChange,
  globalChannel,
  projectChannels,
  directChannels,
  topicChannels,
  teamMembers,
  projects: allProjects,
  onChannelSelect,
  onCreateDirectChannel,
  currentUserId,
  isAdmin,
  projectCallIntent,
  projectCallDataPending = false,
  projectCallDataError = false,
  onProjectCallIntentConsumed,
  onProjectCallRecipientPickerNeeded,
  getChannelDisplayName,
}: ChannelPickerButtonProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [artistFilter, setArtistFilter] = useState<string | null>(null);
  const projectListRef = useRef<HTMLDivElement>(null);
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);

  const { statusConfigs: projectStatusConfigs } = useStatusConfigs('project');
  const statusColorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(projectStatusConfigs)) {
      projectStatusConfigs.forEach((config: any) => {
        map.set(config.statusKey, config.color);
      });
    }
    return map;
  }, [projectStatusConfigs]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const { startCall } = useCall();
  const projectCallIntentConsumer = useRef(createProjectCallIntentConsumer());

  const ensureDirectChannelId = useCallback(async (memberId: number): Promise<string | null> => {
    try {
      const existing = directChannels.find(ch => {
        const memberName = teamMembers.find(m => m.id === memberId)?.firstName
          || teamMembers.find(m => m.id === memberId)?.shortCode
          || `User ${memberId}`;
        return ch.name?.includes(memberName);
      });
      if (existing) return existing.id;
      const res = await apiRequest('POST', `/api/chat/channels/direct/${memberId}`);
      const body = await res.json() as { channel: ChatChannel };
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
      return body.channel.id;
    } catch (e) {
      console.error('[Call] ensureDirectChannelId failed:', e);
      return null;
    }
  }, [directChannels, teamMembers, queryClient]);

  const startDirectCall = useCallback(async (memberId: number, type: 'audio' | 'video') => {
    if (selectedUserIds.size > 0) {
      await performStartGroupCall(channel, globalChannel, type, Array.from(selectedUserIds), {
        startCall,
        onChannelSelect,
        clearSelection: () => setSelectedUserIds(new Set()),
        toast,
      });
      return;
    }
    const channelId = await ensureDirectChannelId(memberId);
    if (!channelId) {
      toast({ title: 'Could not start call', description: 'Failed to open direct channel.', variant: 'destructive' });
      return;
    }
    await startCall({ channelId, type, inviteeUserIds: [memberId] });
  }, [selectedUserIds, channel, globalChannel, onChannelSelect, ensureDirectChannelId, startCall, toast]);

  const startGroupCall = useCallback(async (type: 'audio' | 'video') => {
    const ids = Array.from(selectedUserIds);
    // Channel resolution + stale-state-safe wiring lives in the pure helper
    // performStartGroupCall (see project-call-actions.ts) so it can be tested
    // without mounting the ChatDock tree.
    await performStartGroupCall(channel, globalChannel, type, ids, {
      startCall,
      onChannelSelect,
      clearSelection: () => setSelectedUserIds(new Set()),
      toast,
    });
  }, [selectedUserIds, channel, globalChannel, onChannelSelect, startCall, toast]);

  // Shift modifies selection, not its lifetime. Keep the group selected while
  // the caller releases Shift and moves to a call button.
  
  // Create merged candidates: all projects with optional existing channel
  const projectCandidates = useMemo((): ProjectCandidate[] => {
    const channelMap = new Map<string, ChatChannel>();
    for (const ch of projectChannels) {
      if (ch.projectId) {
        channelMap.set(ch.projectId, ch);
      }
    }
    const memberByCode = new Map<string, TeamMember>();
    for (const m of teamMembers) {
      if (m.shortCode) memberByCode.set(m.shortCode, m);
    }

    return allProjects.map((project) => {
      const existingChannel = channelMap.get(project.id) || null;
      const projectArtistRows = project.artists ?? [];
      const seen = new Set<number>();
      const assignedArtists: TeamMember[] = [];
      // Include the project's lead artist first — they're stored on the
      // project itself (project.leadArtist = shortCode), NOT in
      // project_artists, so iterating only the join rows misses any
      // project whose only assigned person is the lead.
      if (project.leadArtist) {
        const leadMember = memberByCode.get(project.leadArtist);
        if (leadMember && leadMember.id !== currentUserId && !seen.has(leadMember.id)) {
          seen.add(leadMember.id);
          assignedArtists.push(leadMember);
        }
      }
      for (const row of projectArtistRows) {
        const member = memberByCode.get(row.artistCode);
        if (member && member.id !== currentUserId && !seen.has(member.id)) {
          seen.add(member.id);
          assignedArtists.push(member);
        }
      }
      const admins = teamMembers.filter(
        (m) => m.role === 'admin' && m.id !== currentUserId && !seen.has(m.id),
      );
      return {
        projectId: project.id,
        projectName: project.projectName,
        existingChannel,
        lastInteractedAt: existingChannel?.lastInteractedAt || null,
        lastActivitySource: existingChannel?.lastActivitySource || null,
        thumbnail: project.thumbnail || null,
        leadArtist: project.leadArtist || null,
        projectStatus: project.projectStatus || null,
        assignedArtists,
        admins,
      };
    });
  }, [allProjects, projectChannels, teamMembers, currentUserId]);

  const artistColorMap = useMemo(() => {
    const map = new Map<string, string>();
    teamMembers.forEach(member => {
      if (member.shortCode && member.colorCode) {
        map.set(member.shortCode, member.colorCode);
      }
    });
    return map;
  }, [teamMembers]);
  
  // Smart filter and sort logic for project candidates
  // Sort by: 1. Match quality (Exact > Starts With > Contains)
  // 2. Last interaction time (ASC - most recent at bottom)
  const filteredProjectCandidates = useMemo(() => {
    const query = projectSearchQuery.toLowerCase().trim();
    
    let filtered = [...projectCandidates];
    if (artistFilter) {
      filtered = filtered.filter((candidate) => candidate.leadArtist === artistFilter);
    }
    if (query) {
      filtered = filtered.filter((candidate) => {
        const idMatch = candidate.projectId.toLowerCase().includes(query);
        const nameMatch = candidate.projectName.toLowerCase().includes(query);
        return idMatch || nameMatch;
      });
    }
    
    // Helper to get match quality score (higher = better match)
    const getMatchScore = (candidate: ProjectCandidate): number => {
      if (!query) return 0;
      const id = candidate.projectId.toLowerCase();
      const name = candidate.projectName.toLowerCase();
      
      // Exact matches (highest priority)
      if (id === query) return 100;
      if (name === query) return 90;
      
      // Starts with (high priority)
      if (id.startsWith(query)) return 80;
      if (name.startsWith(query)) return 70;
      
      // Contains (lower priority)
      if (id.includes(query)) return 60;
      if (name.includes(query)) return 50;
      
      return 0;
    };
    
    // Sort with match quality priority, then subscription status, then by time (ascending = recent at bottom)
    return filtered.sort((a, b) => {
      if (query) {
        const scoreA = getMatchScore(a);
        const scoreB = getMatchScore(b);
        if (scoreA !== scoreB) return scoreB - scoreA;
      }
      
      const aSubscribed = !!a.existingChannel;
      const bSubscribed = !!b.existingChannel;
      if (aSubscribed !== bSubscribed) {
        return aSubscribed ? 1 : -1;
      }
      
      const timeA = a.lastInteractedAt ? new Date(a.lastInteractedAt).getTime() : 0;
      const timeB = b.lastInteractedAt ? new Date(b.lastInteractedAt).getTime() : 0;
      return timeA - timeB;
    });
  }, [projectCandidates, projectSearchQuery, artistFilter]);
  
  // Mutation to get or create project channel
  const getOrCreateChannelMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await apiRequest('POST', '/api/chat/channels/project', { projectId });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create channel');
      }
      return response.json();
    },
    onSuccess: (newChannel) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
      onChannelSelect(newChannel.id);
      setIsCreatingChannel(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setIsCreatingChannel(false);
    }
  });
  
  const unsubscribeChannelMutation = useMutation({
    mutationFn: async ({ channelId }: { channelId: string; channelName: string }) => {
      const response = await apiRequest('DELETE', `/api/chat/channels/${channelId}/members/${currentUserId}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unsubscribe');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
      queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
      
      if (variables.channelId === channel?.id) {
        const remaining = projectChannels.filter(c => c.id !== variables.channelId);
        if (remaining.length > 0) {
          const firstOther = remaining[0];
          onChannelSelect(firstOther.id);
          toast({ title: 'Abgemeldet', description: `Gewechselt zu ${(firstOther as any).projectName || (firstOther as any).name || firstOther.id}` });
        } else {
          toast({ title: 'Abgemeldet', description: `Abgemeldet von ${variables.channelName}` });
        }
      } else {
        toast({ title: 'Abgemeldet', description: `Abgemeldet von ${variables.channelName}` });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const handleUnsubscribe = useCallback((e: React.MouseEvent, candidate: ProjectCandidate) => {
    e.stopPropagation();
    if (!candidate.existingChannel) return;
    unsubscribeChannelMutation.mutate({
      channelId: candidate.existingChannel.id,
      channelName: candidate.projectName || candidate.projectId || candidate.existingChannel.id,
    });
  }, [unsubscribeChannelMutation]);

  // Per-project popover state for the Phone/Video buttons on project rows.
  // Keyed by `${projectId}:${type}` so opening a different row/modality closes
  // the previous one. Anchored to the row by Radix Popover via candidate id.
  const [artistPopover, setArtistPopover] = useState<{ projectId: string; type: 'audio' | 'video' } | null>(null);
  // Admins the caller UNticked in the popover ("include admins" is on by
  // default, so we only remember opt-outs; reset whenever the popover closes).
  const [excludedAdminIds, setExcludedAdminIds] = useState<Set<number>>(new Set());
  const artistPopoverKey = artistPopover ? `${artistPopover.projectId}:${artistPopover.type}` : null;
  useEffect(() => {
    // Every popover identity (project+modality) starts with all admins ON.
    setExcludedAdminIds(new Set());
  }, [artistPopoverKey]);
  const includedAdminIds = useCallback(
    (candidate: ProjectCandidate) => candidate.admins.map((a) => a.id).filter((id) => !excludedAdminIds.has(id)),
    [excludedAdminIds],
  );
  useEffect(() => {
    if (!pickerOpen) setArtistPopover(null);
  }, [pickerOpen]);

  const ensureProjectChannelId = useCallback(async (candidate: ProjectCandidate): Promise<string | null> => {
    if (candidate.existingChannel) return candidate.existingChannel.id;
    try {
      const res = await apiRequest('POST', '/api/chat/channels/project', { projectId: candidate.projectId });
      const newChannel = await res.json() as ChatChannel;
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
      return newChannel.id;
    } catch (e) {
      console.error('[Call] ensureProjectChannelId failed:', e);
      return null;
    }
  }, [queryClient]);

  const startProjectChannelCall = useCallback(async (
    candidate: ProjectCandidate,
    type: 'audio' | 'video',
    inviteeUserIds: number[],
  ) => {
    await performStartProjectChannelCall(candidate, type, inviteeUserIds, {
      ensureChannelId: ensureProjectChannelId,
      startCall,
      toast,
    });
  }, [ensureProjectChannelId, startCall, toast]);

  // Start from inside the popover: on a limit rejection keep the popover and
  // the caller's selections so they can fix the recipient set.
  const startFromPopover = useCallback((
    candidate: ProjectCandidate,
    type: 'audio' | 'video',
    ids: number[],
  ) => {
    if (exceedsCallParticipantLimit(ids)) {
      toast(GROUP_CALL_LIMIT_TOAST);
      return;
    }
    setSelectedUserIds(new Set());
    setArtistPopover(null);
    void startProjectChannelCall(candidate, type, ids);
  }, [startProjectChannelCall, toast]);

  const handleProjectRowCallClick = useCallback((
    e: React.MouseEvent,
    candidate: ProjectCandidate,
    type: 'audio' | 'video',
    forceFreshAction = false,
  ) => {
    e.stopPropagation();
    const action = decideProjectRowCallAction(candidate, type, forceFreshAction ? null : artistPopover);
    if (action.kind === 'noop') return;
    if (action.kind === 'start') {
      void startProjectChannelCall(candidate, type, action.inviteeUserIds);
      setArtistPopover(null);
      return;
    }
    setArtistPopover((prev) => {
      // Whenever we switch to a different project's popover (or close it),
      // wipe any in-progress shift-select to avoid leaking selections from
      // another project's artist set into this project's group call.
      if (!prev || prev.projectId !== candidate.projectId) {
        setSelectedUserIds(new Set());
      }
      return action.kind === 'close-popover'
        ? null
        : { projectId: action.projectId, type: action.type };
    });
  }, [artistPopover, startProjectChannelCall]);

  useEffect(() => {
    if (!projectCallIntent) return;

    const dataPending = projectCallDataPending;
    if (dataPending) return;
    if (!projectCallIntentConsumer.current.claim(projectCallIntent.requestId)) return;

    // Remove the event intent before taking any side effect. A channels/projects
    // refetch must never replay an outbound call after its first resolution.
    onProjectCallIntentConsumed?.(projectCallIntent.requestId);

    if (projectCallDataError) {
      toast({
        title: 'Could not start video call',
        description: 'Project call data could not be loaded. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    const resolution = resolveProjectCallIntent(projectCallIntent, projectCandidates, true);
    if (resolution.kind === 'unavailable') {
      toast({
        title: 'Could not start video call',
        description: `Project ${projectCallIntent.projectId} is unavailable or you no longer have access.`,
        variant: 'destructive',
      });
      return;
    }
    if (resolution.kind === 'no-invitees') {
      toast({
        title: 'Nobody to call',
        description: `No one else is available to call for ${resolution.candidate.projectId}.`,
        variant: 'destructive',
      });
      return;
    }
    if (resolution.kind !== 'dispatch') return;

    if (resolution.opensRecipientPicker) {
      onProjectCallRecipientPickerNeeded?.();
    }
    // This is the same entry point used by the project row buttons. The
    // no-op synthetic event is only needed because an external shortcut has
    // no row event to propagate from.
    handleProjectRowCallClick(
      { stopPropagation: () => {} } as React.MouseEvent,
      resolution.candidate,
      resolution.type,
      true,
    );
  }, [
    projectCallIntent,
    projectCallDataPending,
    projectCallDataError,
    projectCandidates,
    onProjectCallIntentConsumed,
    onProjectCallRecipientPickerNeeded,
    handleProjectRowCallClick,
    toast,
  ]);

  // Handle project candidate selection
  const handleProjectCandidateSelect = useCallback((candidate: ProjectCandidate) => {
    if (candidate.existingChannel) {
      onChannelSelect(candidate.existingChannel.id);
    } else {
      setIsCreatingChannel(true);
      getOrCreateChannelMutation.mutate(candidate.projectId);
    }
  }, [onChannelSelect, getOrCreateChannelMutation]);
  
  // Auto-scroll to bottom when picker opens (recent items at bottom)
  useEffect(() => {
    if (pickerOpen && panel === 'left' && projectListRef.current) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        projectListRef.current?.scrollTo({
          top: projectListRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 50);
    }
  }, [pickerOpen, panel]);
  
  // Clear search when picker closes
  useEffect(() => {
    if (!pickerOpen) {
      setProjectSearchQuery('');
      setArtistFilter(null);
      setSelectedUserIds(new Set());
    }
  }, [pickerOpen]);

  const createTopicMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiRequest('POST', '/api/chat/channels/topics', { name });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create topic');
      }
      return response.json();
    },
    onSuccess: (channel) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels/topics'] });
      toast({ title: 'Topic created', description: `Created topic: ${channel.name}` });
      setNewTopicName('');
      setIsCreatingTopic(false);
      onChannelSelect(channel.id);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const deleteTopicMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const response = await apiRequest('DELETE', `/api/chat/channels/topics/${channelId}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete topic');
      }
      return channelId;
    },
    onSuccess: (deletedChannelId) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels/topics'] });
      toast({ title: 'Topic deleted' });
      if (channel?.id === deletedChannelId && globalChannel) {
        onChannelSelect(globalChannel.id);
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const muteTopicMutation = useMutation({
    mutationFn: async ({ channelId, muted }: { channelId: string; muted: boolean }) => {
      const response = await apiRequest('POST', `/api/chat/channels/${channelId}/mute`, { muted });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update mute setting');
      }
      return response.json();
    },
    onMutate: async ({ channelId, muted }) => {
      await queryClient.cancelQueries({ queryKey: ['/api/chat/channels/topics'] });
      const previous = queryClient.getQueryData<ChatChannel[]>(['/api/chat/channels/topics']);
      queryClient.setQueryData<ChatChannel[]>(['/api/chat/channels/topics'], (old) =>
        old?.map(ch => ch.id === channelId ? { ...ch, isMuted: muted } : ch) ?? []
      );
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['/api/chat/channels/topics'], context.previous);
      }
      toast({ title: 'Error', description: 'Failed to update notification setting', variant: 'destructive' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/channels/topics'] });
    }
  });

  const handleToggleMute = (e: React.MouseEvent, channelId: string, currentlyMuted: boolean) => {
    e.stopPropagation();
    muteTopicMutation.mutate({ channelId, muted: !currentlyMuted });
  };

  const handleCreateTopic = () => {
    if (newTopicName.trim()) {
      createTopicMutation.mutate(newTopicName.trim());
    }
  };

  const handleDeleteTopic = (e: React.MouseEvent, channelId: string) => {
    e.stopPropagation();
    deleteTopicMutation.mutate(channelId);
  };

  // Topic channel auto-join is handled centrally in handleChannelSelect
  const handleTopicSelect = (channelId: string) => {
    onChannelSelect(channelId);
  };

  // Left panel: Projects only (no tabs)
  if (panel === 'left') {
    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1"
          onClick={onPickerOpen}
          data-testid={`button-channel-picker-${panel}`}
        >
          <FolderKanban className="h-3 w-3 text-primary" />
          <span className="max-w-[80px] truncate">{getChannelDisplayName(channel)}</span>
          {(channel?.unreadCount ?? 0) > 0 && (
            <span className={cn(
              "text-[9px] px-1 py-0.5 rounded-full min-w-[16px] text-center",
              channel?.type === 'client' ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground"
            )}>
              {channel?.unreadCount}
            </span>
          )}
        </Button>

        <AnimatePresence>
          {pickerOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full mb-1 z-50 left-0 w-[680px] bg-card border border-border rounded-lg shadow-xl"
              data-testid={`picker-${panel}`}
            >
              <div className="px-2 py-1.5 border-b border-border sticky top-0 bg-card z-10">
                <div className="flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      className="h-7 pl-7 pr-2 text-xs bg-background border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground"
                      autoFocus
                    />
                  </div>
                  {artistFilter && (
                    <button
                      className="flex items-center gap-1 h-7 px-2 text-[10px] font-bold text-white rounded shrink-0 hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: artistColorMap.get(artistFilter) || '#6B7280', textShadow: '0px 1px 2px rgba(0,0,0,0.8)' }}
                      onClick={() => setArtistFilter(null)}
                      title="Clear artist filter"
                    >
                      {artistFilter}
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              </div>
              <div
                ref={projectListRef}
                className="max-h-56 overflow-y-auto bg-card"
              >
                <div className="p-1">
                  {isCreatingChannel && (
                    <div className="text-xs text-muted-foreground p-2 text-center flex items-center justify-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Opening chat...
                    </div>
                  )}
                  {!isCreatingChannel && filteredProjectCandidates.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-2 text-center">
                      {projectSearchQuery ? 'No matching projects' : 'No projects available'}
                    </div>
                  ) : !isCreatingChannel ? (
                    <>
                      {filteredProjectCandidates.map((candidate) => {
                        const artistColor = candidate.leadArtist ? artistColorMap.get(candidate.leadArtist) || '#6B7280' : null;
                        const timeAgo = candidate.lastInteractedAt ? formatCompactTimeAgo(candidate.lastInteractedAt) : null;
                        const statusColor = candidate.projectStatus ? statusColorMap.get(candidate.projectStatus) || '#6B7280' : null;
                        return (
                          <button
                            key={candidate.projectId}
                            className={cn(
                              "w-full flex items-center gap-2 px-2.5 min-h-[36px] py-1 text-xs rounded hover:bg-accent transition-colors",
                              candidate.existingChannel?.id === channel?.id && "bg-accent"
                            )}
                            onClick={() => handleProjectCandidateSelect(candidate)}
                          >
                            {candidate.thumbnail ? (
                              <img
                                src={candidate.thumbnail}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                className="h-7 w-auto max-w-[48px] rounded-sm object-cover shrink-0 bg-gray-800 ring-1 ring-white/10"
                              />
                            ) : (
                              <FolderKanban className={cn(
                                "w-7 h-7 p-1 shrink-0 rounded-sm bg-gray-800/50",
                                candidate.existingChannel ? "text-primary" : "text-muted-foreground"
                              )} />
                            )}
                            <span className={cn(
                              "truncate flex-1 text-left min-w-0",
                              !candidate.existingChannel && "text-muted-foreground"
                            )}>
                              {candidate.projectId}
                              {` - ${candidate.projectName}`}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                              {artistColor && candidate.leadArtist ? (
                                <span
                                  className={cn(
                                    "text-[10px] w-[32px] text-center px-1 py-0.5 rounded font-bold text-white cursor-pointer hover:opacity-80 transition-opacity",
                                    artistFilter === candidate.leadArtist && "ring-1 ring-white"
                                  )}
                                  style={{ backgroundColor: artistColor, textShadow: '0px 1px 2px rgba(0,0,0,0.8)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setArtistFilter(prev => prev === candidate.leadArtist ? null : candidate.leadArtist);
                                  }}
                                  title={artistFilter === candidate.leadArtist ? `Clear filter: ${candidate.leadArtist}` : `Filter by: ${candidate.leadArtist}`}
                                >
                                  {candidate.leadArtist}
                                </span>
                              ) : (
                                <span className="w-[32px]" />
                              )}
                              {timeAgo ? (
                                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground whitespace-nowrap w-[38px] justify-end" title={candidate.lastActivitySource === 'work' ? 'Last activity: pulse/task' : 'Last activity: chat message'}>
                                  {candidate.lastActivitySource === 'work' ? (
                                    <Activity className="h-2.5 w-2.5 text-amber-400 shrink-0" />
                                  ) : (
                                    <MessageSquare className="h-2.5 w-2.5 text-blue-400 shrink-0" />
                                  )}
                                  {timeAgo}
                                </span>
                              ) : (
                                <span className="w-[38px]" />
                              )}
                              {(candidate.existingChannel?.unreadCount ?? 0) > 0 && (
                                <span className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full shrink-0 min-w-[16px] text-center">
                                  {candidate.existingChannel!.unreadCount}
                                </span>
                              )}
                              {statusColor ? (
                                <span
                                  className="w-[10px] h-[10px] rounded-full shrink-0"
                                  style={{ backgroundColor: statusColor }}
                                  title={candidate.projectStatus || ''}
                                />
                              ) : (
                                <span className="w-[10px]" />
                              )}
                              {candidate.existingChannel ? (
                                <button
                                  className="w-[20px] h-[20px] flex items-center justify-center shrink-0 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                                  onClick={(e) => handleUnsubscribe(e, candidate)}
                                  title="Unsubscribe from project channel"
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                              ) : (
                                <span className="w-[20px] flex items-center justify-center shrink-0">
                                  <Plus className="h-3 w-3 text-muted-foreground" />
                                </span>
                              )}
                              {(() => {
                                const hasArtists = candidate.assignedArtists.length > 0 || candidate.admins.length > 0;
                                const adminsOnly = candidate.assignedArtists.length === 0;
                                const directName = adminsOnly
                                  ? (candidate.admins.length === 1
                                    ? (candidate.admins[0].firstName || candidate.admins[0].shortCode)
                                    : 'admins')
                                  : null;
                                const phoneActive = artistPopover?.projectId === candidate.projectId && artistPopover.type === 'audio';
                                const videoActive = artistPopover?.projectId === candidate.projectId && artistPopover.type === 'video';
                                return (
                                  <Popover
                                    open={phoneActive || videoActive}
                                    onOpenChange={(o) => { if (!o) setArtistPopover(null); }}
                                  >
                                    <PopoverTrigger asChild>
                                      <span className="flex items-center gap-1 shrink-0">
                                        <button
                                          type="button"
                                          disabled={!hasArtists}
                                          className={cn(
                                            "h-6 w-6 rounded-full flex items-center justify-center transition-colors",
                                            hasArtists
                                              ? "bg-gray-700/50 hover:bg-emerald-600/70 text-gray-300 hover:text-white"
                                              : "bg-gray-700/20 text-gray-600 cursor-not-allowed",
                                            phoneActive && "bg-emerald-600/70 text-white"
                                          )}
                                          title={hasArtists
                                            ? (directName
                                              ? `Voice call ${directName}`
                                              : 'Voice call assigned artists')
                                            : 'Nobody to call'}
                                          onClick={(e) => handleProjectRowCallClick(e, candidate, 'audio')}
                                        >
                                          <Phone className="h-3 w-3" />
                                        </button>
                                        <button
                                          type="button"
                                          disabled={!hasArtists}
                                          className={cn(
                                            "h-6 w-6 rounded-full flex items-center justify-center transition-colors",
                                            hasArtists
                                              ? "bg-gray-700/50 hover:bg-sky-600/70 text-gray-300 hover:text-white"
                                              : "bg-gray-700/20 text-gray-600 cursor-not-allowed",
                                            videoActive && "bg-sky-600/70 text-white"
                                          )}
                                          title={hasArtists
                                            ? (directName
                                              ? `Video call ${directName}`
                                              : 'Video call assigned artists')
                                            : 'Nobody to call'}
                                          onClick={(e) => handleProjectRowCallClick(e, candidate, 'video')}
                                        >
                                          <Video className="h-3 w-3" />
                                        </button>
                                      </span>
                                    </PopoverTrigger>
                                    {(phoneActive || videoActive) && (
                                      <PopoverContent
                                        side="right"
                                        align="start"
                                        sideOffset={6}
                                        className="w-64 p-1 bg-card border border-border"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {selectedUserIds.size > 0 && (
                                          <div className="mx-1 mb-1 px-2 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 flex items-center gap-2">
                                            <span className="text-[11px] text-sky-300 font-medium">
                                              {selectedUserIds.size} selected
                                            </span>
                                            <div className="ml-auto flex items-center gap-1">
                                              <button
                                                type="button"
                                                className="h-6 px-2 rounded text-[11px] bg-emerald-600/80 hover:bg-emerald-500 text-white flex items-center gap-1"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  startFromPopover(candidate, 'audio', withIncludedAdmins(candidate, filterAllowedInvitees(candidate.assignedArtists, selectedUserIds), includedAdminIds(candidate)));
                                                }}
                                                title={`Start group voice call (${selectedUserIds.size})`}
                                              >
                                                <Phone className="h-3 w-3" /> Call ({selectedUserIds.size})
                                              </button>
                                              <button
                                                type="button"
                                                className="h-6 px-2 rounded text-[11px] bg-sky-600/80 hover:bg-sky-500 text-white flex items-center gap-1"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  startFromPopover(candidate, 'video', withIncludedAdmins(candidate, filterAllowedInvitees(candidate.assignedArtists, selectedUserIds), includedAdminIds(candidate)));
                                                }}
                                                title={`Start group video call (${selectedUserIds.size})`}
                                              >
                                                <Video className="h-3 w-3" /> Video ({selectedUserIds.size})
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                        {candidate.admins.length > 0 && (
                                          <div className="mx-1 mb-1 px-2 py-1.5 rounded-md border border-border bg-muted/30" data-testid="project-call-admins">
                                            <div className="flex items-center mb-1">
                                              <span className="text-[10px] text-muted-foreground">
                                                {candidate.assignedArtists.length === 0 ? 'Call admins' : 'Include admins'}
                                              </span>
                                              {candidate.assignedArtists.length === 0 && (
                                                <button
                                                  type="button"
                                                  className={cn(
                                                    "ml-auto h-6 px-2 rounded text-[11px] text-white flex items-center gap-1",
                                                    videoActive ? "bg-sky-600/80 hover:bg-sky-500" : "bg-emerald-600/80 hover:bg-emerald-500",
                                                  )}
                                                  data-testid="project-call-admins-start"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    startFromPopover(candidate, videoActive ? 'video' : 'audio', includedAdminIds(candidate));
                                                  }}
                                                >
                                                  {videoActive ? <Video className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                                                  {videoActive ? 'Video' : 'Call'} ({includedAdminIds(candidate).length})
                                                </button>
                                              )}
                                            </div>
                                            {candidate.admins.map((admin) => {
                                              const adminName = admin.firstName || admin.shortCode || `User ${admin.id}`;
                                              const included = !excludedAdminIds.has(admin.id);
                                              return (
                                                <label
                                                  key={admin.id}
                                                  className="flex items-center gap-2 py-0.5 text-xs cursor-pointer select-none"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    className="h-3 w-3 accent-sky-500"
                                                    checked={included}
                                                    onChange={() => setExcludedAdminIds((prev) => toggleSelectedUserId(prev, admin.id))}
                                                    data-testid={`project-call-admin-${admin.id}`}
                                                  />
                                                  <div
                                                    className="h-4 w-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                                                    style={{ backgroundColor: getUserColor(admin.colorCode) }}
                                                  >
                                                    {(admin.firstName?.[0] || admin.shortCode?.[0] || '?').toUpperCase()}
                                                  </div>
                                                  <span className="truncate" style={admin.colorCode ? { color: admin.colorCode } : undefined}>
                                                    {adminName}
                                                  </span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {candidate.assignedArtists.length > 1 && (
                                          <div className="text-[10px] text-muted-foreground px-2 py-1">
                                            Shift-click to select multiple
                                          </div>
                                        )}
                                        {candidate.assignedArtists.map((artist) => {
                                          const artistName = artist.firstName || artist.shortCode || `User ${artist.id}`;
                                          const isSelected = selectedUserIds.has(artist.id);
                                          return (
                                            <div
                                              key={artist.id}
                                              className={cn(
                                                "flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-accent transition-colors cursor-pointer",
                                                isSelected && "ring-1 ring-sky-400 bg-sky-500/20"
                                              )}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (e.shiftKey) {
                                                  setSelectedUserIds(prev => toggleSelectedUserId(prev, artist.id));
                                                }
                                              }}
                                            >
                                              <div
                                                className="h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                                                style={{ backgroundColor: getUserColor(artist.colorCode) }}
                                              >
                                                {(artist.firstName?.[0] || artist.shortCode?.[0] || '?').toUpperCase()}
                                              </div>
                                              <span
                                                className="truncate font-semibold flex-1"
                                                style={artist.colorCode ? { color: artist.colorCode } : undefined}
                                              >
                                                {artistName}
                                              </span>
                                              <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                  type="button"
                                                  className="h-6 w-6 rounded-full bg-gray-700/50 hover:bg-emerald-600/70 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
                                                  title={`Voice call ${artistName}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    startFromPopover(candidate, 'audio', withIncludedAdmins(candidate, selectedUserIds.size > 0 ? filterAllowedInvitees(candidate.assignedArtists, selectedUserIds) : [artist.id], includedAdminIds(candidate)));
                                                  }}
                                                >
                                                  <Phone className="h-3 w-3" />
                                                </button>
                                                <button
                                                  type="button"
                                                  className="h-6 w-6 rounded-full bg-gray-700/50 hover:bg-sky-600/70 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
                                                  title={`Video call ${artistName}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    startFromPopover(candidate, 'video', withIncludedAdmins(candidate, selectedUserIds.size > 0 ? filterAllowedInvitees(candidate.assignedArtists, selectedUserIds) : [artist.id], includedAdminIds(candidate)));
                                                  }}
                                                >
                                                  <Video className="h-3 w-3" />
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </PopoverContent>
                                    )}
                                  </Popover>
                                );
                              })()}
                            </div>
                          </button>
                        );
                      })}
                    </>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Right panel: Global, Direct, Topics tabs
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs gap-1"
        onClick={onPickerOpen}
        data-testid={`button-channel-picker-${panel}`}
      >
        <span className="max-w-[80px] truncate">{getChannelDisplayName(channel)}</span>
        {(channel?.unreadCount ?? 0) > 0 && (
          <span className={cn(
            "text-[9px] px-1 py-0.5 rounded-full min-w-[16px] text-center",
            channel?.type === 'client' ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground"
          )}>
            {channel?.unreadCount}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full mb-1 z-50 right-0 w-72 bg-card border border-border rounded-lg shadow-xl"
            data-testid={`picker-${panel}`}
          >
            <ScrollArea className="max-h-96">
              <div className="p-1">
                {/* TOPICS section header */}
                <div className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-amber-500 uppercase tracking-wider">
                  <MessageSquare className="h-3 w-3" />
                  Topics
                </div>
                
                {/* Topic channels */}
                {topicChannels.length === 0 && !isCreatingTopic ? (
                  <div className="text-xs text-muted-foreground px-2.5 py-1">
                    No topic channels
                  </div>
                ) : (
                  topicChannels.map((ch) => (
                    <div
                      key={ch.id}
                      className={cn(
                        "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-accent transition-colors cursor-pointer group",
                        ch.id === channel?.id && "bg-accent"
                      )}
                      onClick={() => handleTopicSelect(ch.id)}
                    >
                      <MessageSquare className="h-3 w-3 text-amber-500 flex-shrink-0" />
                      <span className={cn("truncate flex-1", ch.isMuted && "text-muted-foreground")}>{ch.name}</span>
                      {(ch.unreadCount ?? 0) > 0 && (
                        <span className={cn(
                          "text-white text-[10px] px-1.5 py-0.5 rounded-full",
                          ch.isMuted ? "bg-gray-500" : "bg-amber-500"
                        )}>
                          {ch.unreadCount}
                        </span>
                      )}
                      <button
                        className={cn(
                          "p-0.5 rounded transition-colors flex-shrink-0",
                          ch.isMuted 
                            ? "text-muted-foreground hover:text-amber-500" 
                            : "text-amber-500/50 hover:text-amber-500 opacity-0 group-hover:opacity-100"
                        )}
                        onClick={(e) => handleToggleMute(e, ch.id, !!ch.isMuted)}
                        title={ch.isMuted ? "Unmute notifications" : "Mute notifications"}
                      >
                        {ch.isMuted ? (
                          <BellOff className="h-3 w-3" />
                        ) : (
                          <Bell className="h-3 w-3" />
                        )}
                      </button>
                      {isAdmin && isCreatingTopic && (
                        <button
                          className="p-0.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                          onClick={(e) => handleDeleteTopic(e, ch.id)}
                          disabled={deleteTopicMutation.isPending}
                          title="Delete topic"
                        >
                          {deleteTopicMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </div>
                  ))
                )}

                {/* Admin: Create Topic button */}
                {isAdmin && (
                  <div className="mt-1">
                    {isCreatingTopic ? (
                      <div className="flex items-center gap-1 px-1">
                        <Input
                          value={newTopicName}
                          onChange={(e) => setNewTopicName(e.target.value)}
                          placeholder="Topic name..."
                          className="h-6 text-xs flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateTopic();
                            if (e.key === 'Escape') {
                              setIsCreatingTopic(false);
                              setNewTopicName('');
                            }
                          }}
                          autoFocus
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={handleCreateTopic}
                          disabled={!newTopicName.trim() || createTopicMutation.isPending}
                        >
                          {createTopicMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plus className="h-3 w-3 text-primary" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => {
                            setIsCreatingTopic(false);
                            setNewTopicName('');
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-accent transition-colors text-amber-500/70 hover:text-amber-500"
                        onClick={() => setIsCreatingTopic(true)}
                      >
                        <Plus className="h-3 w-3" />
                        <span>Create Topic</span>
                      </button>
                    )}
                  </div>
                )}
                
                {/* Visual separator */}
                <div className="my-2 border-t border-border/50" />
                
                {/* DIRECT section header */}
                <div className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">
                  <Users className="h-3 w-3" />
                  Direct
                  <span className="ml-auto text-[9px] text-muted-foreground font-normal normal-case tracking-normal">
                    Shift-click for group call
                  </span>
                </div>

                {/* Group call pill bar (selection persists after Shift release) */}
                {selectedUserIds.size > 0 && (
                  <div className="mx-2 my-1 px-2 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 flex items-center gap-2">
                    <span className="text-[11px] text-sky-300 font-medium">
                      {selectedUserIds.size} selected
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      <button
                        type="button"
                        className="h-6 px-2 rounded text-[11px] bg-emerald-600/80 hover:bg-emerald-500 text-white flex items-center gap-1"
                        onClick={() => { void startGroupCall('audio'); }}
                        title="Start group voice call"
                      >
                        <Phone className="h-3 w-3" /> Call
                      </button>
                      <button
                        type="button"
                        className="h-6 px-2 rounded text-[11px] bg-sky-600/80 hover:bg-sky-500 text-white flex items-center gap-1"
                        onClick={() => { void startGroupCall('video'); }}
                        title="Start group video call"
                      >
                        <Video className="h-3 w-3" /> Video
                      </button>
                      <button
                        type="button"
                        className="h-6 w-6 rounded text-[11px] hover:bg-accent text-muted-foreground flex items-center justify-center"
                        onClick={() => setSelectedUserIds(new Set())}
                        title="Clear selection"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Team members for direct messaging */}
                {teamMembers.filter(m => m.id !== currentUserId).map((member) => {
                  const memberName = member.firstName || member.shortCode || `User ${member.id}`;
                  const directChannel = directChannels.find(
                    ch => ch.name?.includes(memberName)
                  );
                  return (
                    <button
                      key={member.id}
                      className={cn(
                        "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-accent transition-colors",
                        directChannel?.id === channel?.id && "bg-accent",
                        selectedUserIds.has(member.id) && "ring-1 ring-sky-400 bg-sky-500/20"
                      )}
                      onClick={async (e) => {
                        if (e.shiftKey) {
                          setSelectedUserIds(prev => {
                            const next = new Set(prev);
                            if (next.has(member.id)) {
                              next.delete(member.id);
                            } else {
                              next.add(member.id);
                            }
                            return next;
                          });
                          return;
                        }
                        if (directChannel) {
                          onChannelSelect(directChannel.id);
                        } else {
                          await onCreateDirectChannel(member.id);
                        }
                      }}
                    >
                      <div className="relative shrink-0">
                        <div 
                          className="h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: getUserColor(member.colorCode) }}
                        >
                          {(member.firstName?.[0] || member.shortCode?.[0] || '?').toUpperCase()}
                        </div>
                        <StatusBubble userId={member.id} size="sm" showTooltip={false} className="absolute -bottom-0.5 -right-0.5" />
                      </div>
                      <span 
                        className="truncate font-semibold"
                        style={member.colorCode ? { color: member.colorCode } : undefined}
                      >
                        {memberName}
                      </span>
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          className="h-6 w-6 rounded-full bg-gray-700/50 hover:bg-emerald-600/70 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
                          title={`Voice call ${memberName}`}
                          onClick={(e) => { e.stopPropagation(); void startDirectCall(member.id, 'audio'); }}
                        >
                          <Phone className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          className="h-6 w-6 rounded-full bg-gray-700/50 hover:bg-sky-600/70 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
                          title={`Video call ${memberName}`}
                          onClick={(e) => { e.stopPropagation(); void startDirectCall(member.id, 'video'); }}
                        >
                          <Video className="h-3 w-3" />
                        </button>
                      </div>
                    </button>
                  );
                })}
                
                {/* Global Chat at bottom */}
                {globalChannel && (
                  <button
                    className={cn(
                      "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-accent transition-colors",
                      globalChannel.id === channel?.id && "bg-accent"
                    )}
                    onClick={() => onChannelSelect(globalChannel.id)}
                  >
                    <Globe className="h-3 w-3 text-primary flex-shrink-0" />
                    <span>Global Chat</span>
                    <span className="ml-1 text-[9px] bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                      All
                    </span>
                    {(globalChannel.unreadCount ?? 0) > 0 && (
                      <span className="ml-auto bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full">
                        {globalChannel.unreadCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
